import { z } from 'zod';
import { Instant, Locale, LocalDate, LocalTime, MinorAmount, PageQuery, Phone, TimeZone, Uuid, problemDetails, } from './common.js';
import { AppointmentServiceView, AppointmentStatus, BookingSource, CancellationActor, RescheduleAppointmentRequest, } from './booking.js';
import { PROBLEM_TYPE_PREFIX } from './errors.js';
// Контракт КАБИНЕТА САЛОНА: журнал дня, управление записью изнутри салона, база клиентов
// и справочники. Вход, сессия и права — в ./cabinet-auth.ts.
//
// ЧЕМ КАБИНЕТ ПРИНЦИПИАЛЬНО ОТЛИЧАЕТСЯ ОТ ВИТРИНЫ, и почему нельзя было обойтись
// схемами из ./booking.ts:
//
// 1. АДМИНИСТРАТОР ЗНАЕТ КЛИЕНТА. Он указывает существующую карточку по id, видит счётчики
//    неявок, заметки и ограничения. На витрине человек про себя ничего доказать не может,
//    и там клиент — это имя с телефоном.
// 2. ПРАВИЛА САЛОНА ЗДЕСЬ ПРЕДУПРЕЖДЕНИЯ, А НЕ ЗАПРЕТЫ. Витрина отказывает по чёрному
//    списку, горизонту и лимиту записей; администратор за стойкой знает ситуацию лучше
//    правила и вправе его нарушить ОСОЗНАННО — поэтому мутации несут список подтверждённых
//    предупреждений, и он попадает в аудит. Жёстко запрещено только физически невозможное:
//    двойная занятость мастера и ресурса (ADR-006), её держит EXCLUDE в базе.
// 3. РЯДОМ РАБОТАЮТ ЛЮДИ. Один и тот же визит правят администратор в кабинете, мастер
//    с телефона и клиент через бота. Поэтому у мутаций есть expectedUpdatedAt: «последний
//    победил» в журнале означает молча стёртый перенос и потерянного клиента.
// 4. ЭТО РОЛЬ planerius_app, А НЕ planerius_edge. Кабинет читает базу клиентов и постоянную
//    занятость, к которым у роли витрины прав нет вовсе (ADR-005) — в этом и смысл разделения.
//
// Всё, что можно было взять из ./booking.ts, взято импортом: статусы, источник записи,
// инициатор отмены, снимок услуги в записи и тело переноса. Второго описания одной и той же
// операции быть не должно — иначе поведение витрины и кабинета разойдётся на первой правке.
// ───────────────────────── общие примитивы кабинета ─────────────────────────
/**
 * Снимок версии строки (её updated_at), с которым клиент начал редактирование.
 *
 * Оптимистическая блокировка вместо «последний победил»: PgBouncer в transaction mode
 * не позволяет держать блокировку между запросами (ADR-005), а держать её и не надо —
 * достаточно отказаться писать поверх чужой правки и показать человеку, что изменилось.
 */
export const ExpectedVersion = Instant;
/**
 * Телефон, показанный маской: «+7 916 ••• ••-67».
 *
 * Отдельный тип, а не Phone, потому что это НЕ телефон: по маске нельзя позвонить,
 * её нельзя сохранить в карточку и нельзя сравнить с другой маской. Полный номер
 * приходит только тем, у кого есть право clients.phone.view, и для роли мастера —
 * только по явному действию «показать», которое пишется в аудит (GAPS E3).
 */
export const MaskedPhone = z.string().min(3).max(32);
/** Цвет колонки мастера и метки клиента: «#RRGGBB» либо «#RRGGBBAA». */
export const ColorHex = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, 'ожидается цвет вида #1E88E5');
/** Порядок в списках кабинета. Отрицательные значения допустимы: «поднять наверх». */
export const SortOrder = z.number().int().min(-32768).max(32767);
/**
 * Предупреждения, которые администратор подтверждает осознанно.
 *
 * Это не ошибки: система работает правильно, просто человек за стойкой решил иначе,
 * чем правило салона. Коды нужны, чтобы (а) интерфейс показал последствия словами
 * до нажатия кнопки и (б) в аудите осталось, что именно человек подтвердил.
 */
export const CabinetWarningCode = z.enum([
    'client_blacklisted',
    'client_no_show_threshold',
    'client_active_limit_reached',
    'beyond_booking_horizon',
    'below_min_lead',
    'outside_employee_workday',
    // Время мастера уже занято. Код добавлен вместе с пакетной заменой мастера
    // (ReassignEmployeeDayResult.conflicts): там это самая частая причина, по которой
    // визит не переехал, и без отдельного кода она неотличима от «нет смены» —
    // а действия у них разные (подвинуть визит против поправить график).
    // Поле nearestFreeAt в CabinetWarning предназначено именно для него.
    'employee_busy_at_that_time',
    'branch_closed_that_day',
    'employee_service_not_assigned',
    'price_overridden',
    'past_appointment_edit',
    // Номер записи из запроса пакетной замены мастера, которой нет среди действующих
    // записей этого мастера в этот день: её уже перенесли, отменили или отдали другому,
    // пока экран был открыт. Раньше такие номера молча пропадали из ответа.
    'appointment_not_on_that_day',
]);
/**
 * Одно предупреждение с ровно тем контекстом, который нужен интерфейсу для внятной фразы.
 * Свободных полей нет намеренно: «вот его другие записи» раскрыло бы визиты человека тому,
 * кто просто знает номер телефона.
 */
export const CabinetWarning = z.object({
    code: CabinetWarningCode,
    // Готовая фраза на языке кабинета. Клиент вправе показать свою, но чаще показывает эту:
    // формулировки последствий («у него станет 3 неявки») знает сервер, а не интерфейс.
    message: z.string(),
    noShowCount: z.number().int().nonnegative().optional(),
    limit: z.number().int().positive().optional(),
    deadlineAt: Instant.optional(),
    /** Для конфликта занятости — ближайшее свободное окно, чтобы дать выход, а не отказ. */
    nearestFreeAt: Instant.optional(),
});
export const CABINET_PROBLEM_CONFIRMATION_REQUIRED = `${PROBLEM_TYPE_PREFIX}confirmation-required`;
export const CABINET_PROBLEM_VERSION_CONFLICT = `${PROBLEM_TYPE_PREFIX}version-conflict`;
/**
 * Ответ «так нельзя, пока не подтвердишь». Клиент показывает последствия и повторяет
 * запрос, перечислив коды в acknowledgedWarnings. Второй попытки с тем же телом
 * и теми же кодами сервер уже не остановит.
 */
export const cabinetConfirmationProblem = problemDetails.extend({
    code: z.literal('confirmation_required'),
    warnings: z.array(CabinetWarning).min(1),
});
/**
 * Ответ «пока вы правили, это изменил кто-то другой». Отдаётся вместе с актуальной версией,
 * чтобы интерфейс мог показать «Запись изменили» и предложить перечитать, а не перерисоваться
 * молча под руками у человека.
 */
export const cabinetVersionConflictProblem = problemDetails.extend({
    code: z.literal('version_conflict'),
    currentUpdatedAt: Instant,
    changedBy: z.string().nullable(),
});
// ───────────────────────── журнал: запрос ─────────────────────────
/**
 * Запрос расписания на период.
 *
 * ФИЛИАЛ ОБЯЗАТЕЛЕН, режима «все точки сразу» нет. Это продуктовое решение с технической
 * причиной: у каждого филиала своя зона, свой шаг сетки и свои часы работы, и день двух
 * филиалов из разных часовых поясов в одной сетке нечитаем — ни человеком, ни кодом.
 *
 * Границы периода — календарные даты филиала, а не моменты (ADR-007 п.6): «16 сентября»
 * разворачивается в интервал абсолютного времени на стороне PostgreSQL. Прислать сюда
 * «16 сентября 00:00+03» означало бы зафиксировать смещение на клиенте — ровно та ошибка,
 * из-за которой журнал филиала во Владивостоке уезжает на сутки.
 */
export const ScheduleQuery = z
    .object({
    branchId: Uuid,
    dateFrom: LocalDate,
    dateTo: LocalDate,
    /**
     * Мастера. Пусто = все мастера филиала, у кого есть смена в этом периоде.
     * Для роли с restrictToOwnRecords сервер всё равно сузит выдачу до своего сотрудника:
     * фильтр клиента не может РАСШИРИТЬ доступ, только сузить.
     */
    employeeIds: z.array(Uuid).max(100).optional(),
    /** Показывать отменённые визиты бледным слоем — разбор «кто отменил и когда». */
    includeCancelled: z.boolean().default(false),
    /** Зона устройства: нужна только для подписи «у вас сейчас 12:30», не для расчёта. */
    viewerTimeZone: TimeZone.optional(),
})
    .refine((q) => q.dateFrom <= q.dateTo, {
    error: 'dateFrom не может быть позже dateTo',
    path: ['dateTo'],
})
    // Месяц — потолок одного запроса: журнал на квартал по сети мастеров это десятки тысяч
    // строк занятости, которые всё равно не поместятся на экран.
    .refine((q) => Date.parse(q.dateTo) - Date.parse(q.dateFrom) <= 31 * 86_400_000, {
    error: 'период журнала не длиннее 31 дня',
    path: ['dateTo'],
});
// ───────────────────────── журнал: ответ ─────────────────────────
/** Филиал в шапке журнала: всё, без чего сетку нельзя ни нарисовать, ни подписать. */
export const ScheduleBranchView = z.object({
    id: Uuid,
    name: z.string(),
    timezone: TimeZone,
    slotStepMinutes: z.number().int().positive(),
    // Режим упаковки дня (enum packing_mode): от него зависит, показывать ли слоты
    // только по решётке или встык к соседней записи.
    packingMode: z.enum(['grid', 'tight']),
    minLeadMinutes: z.number().int().nonnegative(),
    maxHorizonDays: z.number().int().positive(),
});
/**
 * Один день филиала в периоде.
 *
 * Закрытый день — это ответ, а не отсутствие данных: без явного isClosed интерфейс
 * нарисует пустую сетку и администратор решит, что расписание не загрузилось.
 * calendarHint — подсказка производственного календаря РФ, именно подсказка: решение
 * салона живёт в исключениях графика, а не в календаре (ADR-007 п.9).
 */
export const ScheduleDayView = z.object({
    date: LocalDate,
    isClosed: z.boolean(),
    opensAt: LocalTime.nullable(),
    closesAt: LocalTime.nullable(),
    exceptionReason: z.string().nullable(),
    calendarHint: z
        .object({
        dayType: z.enum(['work', 'short', 'weekend', 'holiday', 'nonworking']),
        title: z.string().nullable(),
    })
        .nullable(),
});
/** Колонка журнала: мастер так, как его видит сотрудник салона, а не клиент витрины. */
export const ScheduleEmployeeView = z.object({
    id: Uuid,
    displayName: z.string(),
    publicName: z.string().nullable(),
    positionName: z.string().nullable(),
    color: ColorHex.nullable(),
    avatarUrl: z.url().nullable(),
    isBookable: z.boolean(),
    /** Уволенный мастер остаётся в прошлых днях: его записи и история никуда не деваются. */
    archivedAt: Instant.nullable(),
});
/**
 * Рабочее окно мастера — «числитель» всей сетки: вне его не бывает ни слотов, ни записей.
 * Отдаётся отдельно от занятости, потому что «мастер не работает» и «мастер занят» —
 * для человека за стойкой два разных ответа с разными действиями.
 */
export const ScheduleWorkdayView = z.object({
    employeeId: Uuid,
    date: LocalDate,
    startAt: Instant,
    endAt: Instant,
    source: z.enum(['pattern', 'manual', 'override']),
    note: z.string().nullable(),
});
/**
 * Вид занятости. Значения повторяют enum busy_kind, где `break` в базе хранится
 * как `break` (в Prisma поле названо break_ только потому, что это ключевое слово).
 */
export const CabinetBusyKind = z.enum([
    'service',
    'buffer',
    'cleanup',
    'break',
    'timeoff',
    'blocked',
    'group_session',
]);
/**
 * Занятость, которая не является записью: буфер, уборка, перерыв, отпуск, блокировка.
 *
 * Флаг isSystem разделяет два принципиально разных объекта, которые в базе лежат в одной
 * таблице: буфер и уборка ПРИНАДЛЕЖАТ соседней записи (их нельзя двигать и удалять отдельно,
 * они исчезнут вместе с ней), а перерыв, отпуск и блокировка самостоятельны. Без этого
 * признака интерфейс разрешит перетащить уборку кресла, и она тут же вернётся обратно.
 */
export const BusyBlockView = z.object({
    id: Uuid,
    kind: CabinetBusyKind,
    /** Ровно одно из двух заполнено: занятость либо мастера, либо ресурса. */
    employeeId: Uuid.nullable(),
    resourceId: Uuid.nullable(),
    branchId: Uuid,
    startAt: Instant,
    endAt: Instant,
    /** Связь со своей записью — по ней буфер рисуется примыкающим к карточке визита. */
    appointmentId: Uuid.nullable(),
    appointmentStepId: Uuid.nullable(),
    note: z.string().nullable(),
    isSystem: z.boolean(),
    createdByName: z.string().nullable(),
    updatedAt: Instant,
});
/**
 * Вид удержания слота. Шире публичного SlotHoldKind из ./booking.ts осознанно: витрине
 * показывать `payment` и `quarantine` незачем, а журнал ОБЯЗАН их показать — иначе
 * администратор видит занятое время без объяснения, кем и почему.
 */
export const CabinetSlotHoldKind = z.enum([
    'checkout',
    'payment',
    'reschedule',
    'waitlist',
    'quarantine',
    'manual',
]);
/**
 * Удержание слота в сетке (GAPS C2).
 *
 * Прямо сейчас живой человек на витрине выбрал это время и ждёт код из SMS. Время ещё
 * не занято записью, но свободным не является: посадив туда клиента по телефону,
 * администратор столкнёт двоих через сорок секунд.
 *
 * expiresAt — абсолютный момент, а не «осталось N секунд»: секунды протухают в пути
 * и в спящем ноутбуке, а обратный отсчёт интерфейс считает сам от текущего времени.
 * isOwn отвечает на единственный вопрос по действиям: чужое удержание трогать нельзя,
 * своё — можно снять.
 */
export const SlotHoldView = z.object({
    id: Uuid,
    kind: CabinetSlotHoldKind,
    branchId: Uuid,
    employeeId: Uuid.nullable(),
    resourceId: Uuid.nullable(),
    startAt: Instant,
    endAt: Instant,
    expiresAt: Instant,
    source: BookingSource,
    isOwn: z.boolean(),
    note: z.string().nullable(),
});
/** Метка клиента в журнале и в списках: цвет задаёт салон, общего справочника нет. */
export const ClientTagBadge = z.object({
    id: Uuid,
    name: z.string(),
    colorHex: ColorHex.nullable(),
});
/** Клиент в карточке визита: минимум, по которому администратор принимает решение. */
export const AppointmentClientBrief = z.object({
    id: Uuid,
    displayName: z.string(),
    // Маска есть всегда, полный номер — только при праве clients.phone.view.
    // Для мастера phone всегда null: номер он получает только действием (RevealClientPhoneRequest).
    phoneMasked: MaskedPhone.nullable(),
    phone: Phone.nullable(),
    /**
     * КАК ЭТОМУ ЗРИТЕЛЮ ДОСТУПЕН ПОЛНЫЙ НОМЕР. Решает сервер, экран только исполняет.
     *
     *   shown      — номер уже в поле phone: право clients.phone.view (администратор, владелец).
     *   on_open    — свой клиент мастера, салон включил «мастер видит телефон» (4-Б12):
     *                номер открывается при открытии карточки визита или нажатием «Позвонить».
     *   on_request — свой клиент мастера, настройка выключена (умолчание): маска
     *                и действие «Показать».
     *   none       — номер этому зрителю не открывается: чужой клиент, нет прав, нет номера.
     *
     * ПОЧЕМУ ДАЖЕ ПРИ ВКЛЮЧЁННОЙ НАСТРОЙКЕ НОМЕР НЕ ПРИХОДИТ ВМЕСТЕ С ДНЁМ. ТЗ требует,
     * чтобы журнал открытий вёлся и в этом положении (04, Б12: «не отменяют журнал
     * открытий телефона»). Номер, пришедший с лентой дня, открыт для всех записей дня
     * разом, хотя человек мог не посмотреть ни на один, — и журнал либо молчит, либо
     * лжёт. Номер, отданный по действию, — это ровно одно открытие и ровно одна строка.
     */
    phoneAccess: z.enum(['shown', 'on_open', 'on_request', 'none']).default('none'),
    visitsCount: z.number().int().nonnegative(),
    noShowCount: z.number().int().nonnegative(),
    /**
     * Когда человек был у салона в последний раз. null — не был ни разу либо счётчики
     * ещё не пересчитаны.
     *
     * ПОЧЕМУ ПРЯМО ЗДЕСЬ, А НЕ ОТДЕЛЬНЫМ ЗАПРОСОМ ЗА КАРТОЧКОЙ КЛИЕНТА. Администратор
     * читает эту строку вслух в трубку («вы были у нас 12 августа»), и на телефоне
     * (5-А3) она стоит рядом с числом визитов. Отдельный запрос за одной датой означал
     * бы пустое место в карточке ровно на те полсекунды, пока он говорит.
     */
    lastVisitAt: Instant.nullable().default(null),
    isBlacklisted: z.boolean(),
    /** Закреплённые заметки (аллергия, формула окрашивания) — их нельзя не прочитать. */
    pinnedNotes: z.array(z.string()).default([]),
    tags: z.array(ClientTagBadge).default([]),
});
/**
 * Шаг составной услуги в журнале. Ради этих двух флагов вся конструкция и существует:
 * occupiesEmployee=false + occupiesResource=true — это пауза окрашивания, в которую
 * мастер берёт другого клиента, а кресло остаётся занятым. Единственное место сетки,
 * где две карточки в одной колонке в одно время — норма, а не конфликт.
 */
export const AppointmentStepView = z.object({
    id: Uuid,
    ord: z.number().int().positive(),
    name: z.string(),
    startAt: Instant,
    endAt: Instant,
    occupiesEmployee: z.boolean(),
    occupiesResource: z.boolean(),
    employeeId: Uuid.nullable(),
    resourceId: Uuid.nullable(),
});
/**
 * Услуга в записи глазами кабинета — та же, что на витрине, но с гасимой ценой.
 *
 * Расширение, а не второе описание: поля, порядок и смысл берутся из AppointmentServiceView
 * (./booking.ts) и расходиться с ним не могут. Отличие ровно одно: `priceMinor` допускает
 * null. Видимость денег мастеру — настройка салона (решение владельца 46), и выключенная
 * настройка означает, что цены в ответе НЕТ, а не что она есть и спрятана в интерфейсе:
 * спрятанное на клиенте всё равно уехало в браузер и видно во вкладке «Сеть».
 *
 * На витрине такого поля быть не может: там цену смотрит сам клиент, и скрывать её не от кого.
 */
export const CabinetAppointmentServiceView = AppointmentServiceView.extend({
    priceMinor: MinorAmount.nullable(),
});
/**
 * Запись глазами салона.
 *
 * От витринного AppointmentView отличается тремя вещами, и каждая из них — причина
 * отдельной схемы, а не «ещё пары полей»: клиент со счётчиками и ограничениями,
 * внутренняя кухня (ресурс, автор записи, шаги) и updatedAt как версия для мутаций.
 * Услуги переиспользуют AppointmentServiceView из ./booking.ts — это один и тот же
 * снимок цены на момент записи, и расходиться ему нельзя.
 */
export const CabinetAppointmentView = z.object({
    id: Uuid,
    status: AppointmentStatus,
    branchId: Uuid,
    /** Пусто у «неразобранной» записи (мастер уволился или салон разбирает заявки вручную). */
    employeeId: Uuid.nullable(),
    resourceIds: z.array(Uuid).default([]),
    /**
     * Названия занятых мест — «кресло 2», «кабинет у окна» (5-А3). Приходят вместе
     * с записью намеренно: справочник ресурсов закрыт правом catalog.view, которого
     * у мастера нет, а своё кресло он обязан видеть в собственной карточке визита.
     */
    resourceNames: z.array(z.string()).default([]),
    startAt: Instant,
    endAt: Instant,
    client: AppointmentClientBrief,
    services: z.array(CabinetAppointmentServiceView).min(1),
    steps: z.array(AppointmentStepView).default([]),
    /**
     * Деньги визита. null означает «этому человеку деньги не показываем» — право
     * `finance.view` либо настройка салона «показывать деньги мастеру» (решение владельца 46).
     * Поля гасятся на сервере, в самой выборке: иначе мастер, открыв журнал, получает
     * дневную выручку филиала целиком, независимо от того, что нарисовано на экране.
     * Гасятся ОБА и вместе с ценами позиций: по сумме минус видимые части считается
     * невидимая, и «частичное скрытие» — это отсутствие скрытия.
     */
    priceMinor: MinorAmount.nullable(),
    prepaidMinor: MinorAmount.nullable(),
    comment: z.string().nullable(),
    source: BookingSource,
    /** Кто завёл: имя сотрудника либо пусто, если записался сам клиент с витрины или бота. */
    createdByName: z.string().nullable(),
    createdAt: Instant,
    cancelReasonId: Uuid.nullable(),
    cancelReasonTitle: z.string().nullable(),
    cancelledAt: Instant.nullable(),
    cancelledBy: CancellationActor.nullable(),
    noShowAt: Instant.nullable(),
    /** Откуда перенесена: перенос — не «отмена плюс создание», историю нельзя терять (GAPS C6). */
    rescheduledFromId: Uuid.nullable(),
    /** Версия для expectedUpdatedAt. Без неё параллельная правка затирается молча. */
    updatedAt: Instant,
});
/**
 * Запись, найденная ПО НОМЕРУ, без знания даты (1-Э1.14).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ОТВЕТ. Ссылку на запись присылают в поддержку, в центр уведомлений
 * и в разборы сводки — а день журнала грузится по дате, которой в ссылке нет. Без этого
 * маршрута кабинет открывал сегодняшний день, не находил там записи и показывал пустую
 * панель: «такой записи нет» вместо «она вчера, вот она».
 *
 * Дата приходит КАЛЕНДАРНАЯ и в зоне филиала: из неё журнал собирает адрес дня.
 * Вычислять её на клиенте из startAt нельзя — зона филиала и зона браузера расходятся,
 * и визит в 00:30 по Москве у смотрящего из Калининграда уехал бы на сутки назад.
 */
export const CabinetAppointmentLookup = z.object({
    appointment: CabinetAppointmentView,
    date: LocalDate,
    branchId: Uuid,
    branchName: z.string(),
});
/** Групповое занятие в сетке: вместо имени клиента — услуга и счётчик мест. */
export const GroupSessionView = z.object({
    id: Uuid,
    serviceId: Uuid,
    serviceName: z.string(),
    branchId: Uuid,
    employeeId: Uuid.nullable(),
    resourceId: Uuid.nullable(),
    startAt: Instant,
    endAt: Instant,
    capacity: z.number().int().positive(),
    bookedCount: z.number().int().nonnegative(),
    /** Ниже этого числа занятие не состоится; салон решает сам, но видеть обязан. */
    minParticipants: z.number().int().nonnegative(),
    /** null — деньги этому человеку не показываются (см. CabinetAppointmentView.priceMinor). */
    priceMinor: MinorAmount.nullable(),
    status: z.enum(['scheduled', 'cancelled', 'completed']),
    updatedAt: Instant,
});
/**
 * Правила записи филиала, действующие в этот момент.
 *
 * Едут вместе с журналом, а не отдельным запросом: диалоги отмены и неявки обязаны
 * показать последствия ДО нажатия кнопки («станет 3 неявки — онлайн-запись закроется»),
 * а считать эти последствия второй раз на клиенте — значит разойтись с сервером.
 */
export const BookingRulesView = z.object({
    branchId: Uuid.nullable(),
    minLeadMinutes: z.number().int().nonnegative(),
    maxHorizonDays: z.number().int().positive(),
    cancelDeadlineMinutes: z.number().int().nonnegative(),
    rescheduleDeadlineMinutes: z.number().int().nonnegative(),
    quarantineMinutes: z.number().int().nonnegative(),
    prepayPercent: z.number().int().min(0).max(100),
    noShowPolicy: z.enum(['none', 'warn', 'require_prepay', 'admin_only', 'block']),
    noShowThreshold: z.number().int().nonnegative(),
    maxActiveAppointments: z.number().int().positive(),
    holdTtlSeconds: z.number().int().positive(),
    waitlistHoldSeconds: z.number().int().positive(),
    allowOnlineCancel: z.boolean(),
    allowOnlineReschedule: z.boolean(),
});
/**
 * УМОЛЧАНИЯ ПЛАТФОРМЫ — ОДИН ИСТОЧНИК.
 *
 * Ими работает салон, который правил записи не настраивал: строки booking_rules у него нет
 * вовсе. Раньше эти числа лежали в четырёх местах сразу — в колонках таблицы, в запасном
 * значении витрины (booking.repository), в представлении branch_booking_params и в INSERT
 * настроек, — и уже разошлись: минимальный лаг в колонке 0, а в коде час. Расхождение
 * видно только в проданном времени, которое начинается раньше, чем клиент успеет доехать.
 *
 * SQL, который эти же значения повторяет (db/operational/11-branch-booking-params.sql),
 * обязан меняться вместе с этим объектом: в представление константы из TypeScript
 * не подставить.
 */
export const PLATFORM_BOOKING_RULES = {
    minLeadMinutes: 60,
    maxHorizonDays: 60,
    cancelDeadlineMinutes: 180,
    rescheduleDeadlineMinutes: 180,
    quarantineMinutes: 0,
    prepayPercent: 0,
    noShowPolicy: 'none',
    noShowThreshold: 2,
    maxActiveAppointments: 3,
    holdTtlSeconds: 300,
    waitlistHoldSeconds: 900,
    allowOnlineCancel: true,
    allowOnlineReschedule: true,
};
/**
 * Строка правил на экране настроек: сами правила, чья это строка и когда её меняли.
 *
 * `isPlatformDefault` — правил нет ни у филиала, ни у салона, и действуют умолчания
 * платформы. Экран обязан это сказать словами: «ничего не настроено» и «настроено ровно
 * так же» выглядят одинаково, а означают разное — первое поменяется, когда мы поменяем
 * умолчания, второе нет.
 */
export const BookingRulesEntry = BookingRulesView.extend({
    branchName: z.string().nullable(),
    isPlatformDefault: z.boolean(),
    /** Версия строки для expectedUpdatedAt. null — строки ещё нет. */
    updatedAt: Instant.nullable(),
});
/** Правила салона и филиалов, которые их переопределили. */
export const BookingRulesSettings = z.object({
    salon: BookingRulesEntry,
    branches: z.array(BookingRulesEntry),
});
/**
 * Расписание за период — один ответ на весь экран журнала.
 *
 * Плоские списки со ссылками по id, а не дерево «мастер → день → записи»: одна и та же
 * запись нужна и колонке мастера, и ленте дня, и панели визита, а две её копии в одном
 * ответе разъедутся при первой же правке.
 *
 * generatedAt обязателен: при потере связи кабинет продолжает показывать загруженный день
 * и честно подписывает его моментом («показан день на 14:35»), вместо того чтобы выглядеть
 * актуальным. Офлайн-очереди изменений в кабинете нет и не планируется — запись, созданная
 * без связи, неразрешимо конфликтует с реальной занятостью (GAPS D4).
 */
export const ScheduleResult = z.object({
    branch: ScheduleBranchView,
    days: z.array(ScheduleDayView),
    employees: z.array(ScheduleEmployeeView),
    workdays: z.array(ScheduleWorkdayView),
    appointments: z.array(CabinetAppointmentView),
    busy: z.array(BusyBlockView),
    holds: z.array(SlotHoldView),
    groupSessions: z.array(GroupSessionView),
    rules: BookingRulesView,
    generatedAt: Instant,
});
// ───────────────────────── управление записью из кабинета ─────────────────────────
/**
 * Согласия, оформленные администратором.
 *
 * Отдельная схема, а не BookingConsents из ./booking.ts, и это не дублирование: на витрине
 * человек САМ ставит галочку, и реквизитом доказательства служат его IP и User-Agent.
 * В кабинете согласие получено устно у стойки или на бумаге, и доказательством является
 * отметка сотрудника и его учётная запись. Это разные юридические факты с разным набором
 * реквизитов, и записывать их одной схемой — значит потом не суметь объяснить РКН, откуда
 * взялось согласие (ADR-033).
 *
 * Согласие на обработку ПД и согласие на рекламу — два РАЗНЫХ документа с 01.09.2025;
 * объединять их в один флаг нельзя, штраф за это платит салон.
 */
export const AdminConsentCapture = z
    .object({
    personalData: z.literal(true),
    personalDataVersion: z.string().min(1).max(64),
    ads: z.boolean().default(false),
    adsVersion: z.string().min(1).max(64).optional(),
    /** Как именно получено: на слово, на бумаге или клиент подписал у нас на экране. */
    capturedVia: z.enum(['verbal', 'paper', 'client_signed']),
})
    .refine((c) => !c.ads || c.adsVersion !== undefined, {
    error: 'для рекламного согласия обязательна версия текста',
    path: ['adsVersion'],
});
/**
 * Кого записываем.
 *
 * ГЛАВНОЕ ОТЛИЧИЕ ОТ ВИТРИНЫ: администратор указывает существующую карточку по id —
 * он нашёл человека по обрывку номера и знает, что это он. Анонимный посетитель витрины
 * так не может: там любой, кто знает чужой телефон, записался бы «за него».
 * Новая карточка заводится здесь же, без перехода в раздел клиентов: администратор
 * держит трубку, и уход с экрана — это потерянная запись.
 */
export const CabinetAppointmentClient = z.union([
    z.object({ clientId: Uuid }),
    z.object({
        displayName: z.string().trim().min(1).max(200),
        phone: Phone,
        locale: Locale.optional(),
        consents: AdminConsentCapture,
    }),
]);
/**
 * Позиция корзины визита. Мастер задаётся на КАЖДОЙ услуге: «стрижка у Ани, потом маникюр
 * у Кати» — одна запись с двумя исполнителями (GAPS C3). Пусто = мастер записи целиком.
 *
 * Длительность и цену переопределяет администратор, а не клиент, и оба переопределения —
 * повод для предупреждения price_overridden и записи в аудит: скидка выше лимита салона
 * это отдельное право, а не свобода поля ввода.
 */
export const CabinetAppointmentItem = z.object({
    serviceId: Uuid,
    employeeId: Uuid.optional(),
    resourceId: Uuid.optional(),
    durationOverrideMinutes: z.number().int().min(5).max(24 * 60).optional(),
    priceOverrideMinor: MinorAmount.optional(),
});
/**
 * Создание записи администратором.
 *
 * Конец визита и итоговая сумма сюда не передаются: их считает сервер по услугам, шагам,
 * буферам и правилам цены. Переданное клиентом endAt было бы способом занять полдня одной
 * стрижкой, а переданная сумма — способом разойтись с прайсом в истории клиента.
 */
export const CabinetCreateAppointmentRequest = z.object({
    branchId: Uuid,
    /** Пусто = «неразобранная» запись: типовой сценарий при увольнении мастера (GAPS C5). */
    employeeId: Uuid.optional(),
    startAt: Instant,
    items: z.array(CabinetAppointmentItem).min(1).max(10),
    client: CabinetAppointmentClient,
    comment: z.string().trim().max(1000).optional(),
    /** Удержание, поставленное администратором, пока он говорил по телефону. */
    holdId: Uuid.optional(),
    /**
     * Предупреждения, которые человек увидел и подтвердил. Пустой список при первом запросе —
     * нормальный ход: сервер ответит confirmation_required с их перечнем, интерфейс покажет
     * последствия, и запрос повторится уже с подтверждением.
     */
    acknowledgedWarnings: z.array(CabinetWarningCode).default([]),
    /** Отправить клиенту подтверждение сразу («Записать и отправить подтверждение»). */
    notifyClient: z.boolean().default(true),
});
/** Правка состава уже созданного визита: услуги, комментарий, мастер записи. */
export const CabinetUpdateAppointmentRequest = z.object({
    appointmentId: Uuid,
    employeeId: Uuid.nullable().optional(),
    items: z.array(CabinetAppointmentItem).min(1).max(10).optional(),
    comment: z.string().trim().max(1000).nullable().optional(),
    acknowledgedWarnings: z.array(CabinetWarningCode).default([]),
    expectedUpdatedAt: ExpectedVersion,
});
/**
 * Перенос из кабинета — то же доменное действие, что на витрине, поэтому тело берётся
 * оттуда и только дополняется. Дополнения ровно три: версия записи (рядом работают люди),
 * подтверждённые предупреждения (администратор вправе нарушить правило) и решение,
 * уведомлять ли клиента — при переносе «на пять минут вперёд» сообщение только пугает.
 */
export const CabinetRescheduleAppointmentRequest = RescheduleAppointmentRequest.extend({
    branchId: Uuid.optional(),
    resourceId: Uuid.optional(),
    acknowledgedWarnings: z.array(CabinetWarningCode).default([]),
    notifyClient: z.boolean().default(true),
    expectedUpdatedAt: ExpectedVersion,
});
/**
 * Отмена из кабинета.
 *
 * reasonCode ОБЯЗАТЕЛЕН (на витрине он необязателен) — и это не формальность: код причины
 * решает три вещи сразу — считать ли отмену неявкой, сгорает ли предоплата и уходит ли
 * время в карантин. Свободный текст этих решений принять не может, поэтому он дополнение,
 * а не замена. Поэтому же схема не наследуется от CancelAppointmentRequest: там правило
 * «причина нужна только салону» уже зашито проверкой, а здесь причина нужна всегда.
 *
 * initiatedBy обязателен и без значения по умолчанию: отмена клиентом тянет его счётчик,
 * отмена салоном — нет, и угадывать это за администратора нельзя.
 */
export const CabinetCancelAppointmentRequest = z.object({
    appointmentId: Uuid,
    initiatedBy: CancellationActor,
    reasonCode: z.string().min(1).max(64),
    comment: z.string().trim().max(1000).optional(),
    notifyClient: z.boolean().default(true),
    expectedUpdatedAt: ExpectedVersion,
});
/**
 * «Клиент пришёл» и «Завершено» — два нажатия без подтверждения, оба обратимы.
 * Отдельная схема от отмены: это не изменение намерения, а фиксация факта, и от неё
 * зависят счётчики клиента и его история.
 */
/**
 * «Подтвердить» — переход из «ждёт подтверждения» в «подтверждена». Тело то же, что у
 * отметки прихода: запись и версия, которую видел человек.
 */
export const ConfirmAppointmentRequest = z.object({
    appointmentId: Uuid,
    expectedUpdatedAt: ExpectedVersion,
});
export const MarkAppointmentArrivedRequest = z.object({
    appointmentId: Uuid,
    expectedUpdatedAt: ExpectedVersion,
});
export const MarkAppointmentCompletedRequest = z.object({
    appointmentId: Uuid,
    expectedUpdatedAt: ExpectedVersion,
    /** Итоговая сумма, если она разошлась с планом (доп. услуга, скидка у стойки). */
    finalPriceMinor: MinorAmount.optional(),
    acknowledgedWarnings: z.array(CabinetWarningCode).default([]),
});
/**
 * Неявка. Отдельное действие, а не разновидность отмены: это репутационная отметка
 * на человеке, и последствия у неё по правилам филиала (noShowPolicy) вплоть до закрытия
 * онлайн-записи. Из этого же диалога клиента можно внести в чёрный список салона —
 * с обязательной причиной, которую прочитает следующий администратор.
 */
export const MarkAppointmentNoShowRequest = z.object({
    appointmentId: Uuid,
    comment: z.string().trim().max(1000).optional(),
    blacklist: z
        .object({
        reason: z.string().trim().min(3).max(1000),
        blocksOnlineBooking: z.boolean().default(true),
        expiresAt: Instant.nullable().default(null),
    })
        .optional(),
    expectedUpdatedAt: ExpectedVersion,
});
/**
 * Снятие отметки неявки. Существует потому, что администраторы ошибаются, а последствия
 * для человека реальны: счётчик, предоплата по правилам салона, закрытая онлайн-запись.
 */
export const UndoAppointmentNoShowRequest = z.object({
    appointmentId: Uuid,
    comment: z.string().trim().max(1000).optional(),
    expectedUpdatedAt: ExpectedVersion,
});
/**
 * Показ полного телефона клиента (GAPS E3).
 *
 * Отдельное действие, а не поле в ответе: мастер видит своих клиентов лично, запретить
 * ему телефон совсем нельзя, но каждое открытие пишется в аудит, и салон видит отчёт
 * «кто сколько контактов открыл за неделю». Это единственный работающий инструмент против
 * тихого выноса базы глазами и скриншотом.
 */
export const RevealClientPhoneRequest = z.object({
    clientId: Uuid,
    /** Из какой записи открыт телефон — в аудите это отличает работу от перебора базы. */
    appointmentId: Uuid.optional(),
    /**
     * ОСНОВАНИЕ показа словами, когда записи нет: «перезвонить по заявке с сайта».
     *
     * Необязательное, и это осознанно: показ телефона нажимают десятки раз за смену,
     * и обязательное поле здесь дало бы не основания, а точку в двухстах строках подряд.
     * Проверяемая величина — не текст, а пара «есть запись ИЛИ есть объяснение»:
     * строка аудита без обоих и есть признак перебора базы глазами (GAPS E3).
     */
    reason: z.string().trim().min(3).max(300).optional(),
});
export const RevealClientPhoneResult = z.object({ phone: Phone, revealedAt: Instant });
/**
 * Занятость, которую заводит человек: перерыв, отпуск, блокировка времени.
 * Буфера и уборки в этом перечислении нет намеренно — их создаёт система вместе с записью,
 * и создать их руками нельзя, иначе они переживут свою запись и навсегда съедят время.
 */
export const ManualBusyKind = z.enum(['break', 'timeoff', 'blocked']);
export const CreateBusyBlockRequest = z.object({
    branchId: Uuid,
    kind: ManualBusyKind,
    /** Ровно одно из двух: блокируем либо мастера, либо ресурс. */
    employeeId: Uuid.optional(),
    resourceId: Uuid.optional(),
    startAt: Instant,
    endAt: Instant,
    note: z.string().trim().max(500).optional(),
    acknowledgedWarnings: z.array(CabinetWarningCode).default([]),
});
export const UpdateBusyBlockRequest = z.object({
    busyId: Uuid,
    startAt: Instant.optional(),
    endAt: Instant.optional(),
    note: z.string().trim().max(500).nullable().optional(),
    expectedUpdatedAt: ExpectedVersion,
});
export const DeleteBusyBlockRequest = z.object({
    busyId: Uuid,
    expectedUpdatedAt: ExpectedVersion,
});
/**
 * Многодневное отсутствие мастера задаётся КАЛЕНДАРНЫМИ ДАТАМИ филиала, а не моментами:
 * «отпуск с 1 по 14 октября» — это про календарь, и разворачивать его в абсолютное время
 * обязан PostgreSQL по зоне филиала. Посчитанная на клиенте полночь даёт сдвиг на час
 * ровно в тех регионах, которые меняли зону (ADR-007).
 */
/**
 * «Отпроситься» (5-М6, вариант А): просьба мастера уходит делом в ленту администратора,
 * он сам оформляет отсутствие. Состояний «согласовано / отклонено» нет — их негде хранить
 * и некому выставлять: решение администратора и есть оформленное отсутствие.
 */
export const MasterTimeOffReason = z.enum(['vacation', 'sick', 'day_off']);
export const MasterTimeOffPreviewQuery = z.object({ branchId: Uuid, dateFrom: LocalDate, dateTo: LocalDate });
/** «Цена просьбы — до отправки»: смены и клиенты, которых придётся перенести. */
export const MasterTimeOffPreview = z.object({
    shifts: z.number().int().nonnegative(),
    appointments: z.number().int().nonnegative(),
    days: z.array(z.object({ date: LocalDate, appointments: z.number().int().positive() })),
    /** Период пересекается с уже оформленным отсутствием — отправлять нечего. */
    overlapsTimeOff: z.boolean(),
});
export const MasterTimeOffRequest = z
    .object({
    branchId: Uuid,
    dateFrom: LocalDate,
    dateTo: LocalDate,
    reason: MasterTimeOffReason,
    note: z.string().trim().max(300).optional(),
})
    .refine((r) => r.dateFrom <= r.dateTo, { error: 'дата начала позже даты окончания', path: ['dateTo'] });
export const MasterTimeOffRequestView = z.object({
    id: Uuid,
    dateFrom: LocalDate,
    dateTo: LocalDate,
    body: z.string(),
    /** sent — ждёт администратора; done — отсутствие оформлено; recalled — мастер отозвал. */
    state: z.enum(['sent', 'done', 'recalled']),
    createdAt: Instant,
});
export const MasterTimeOffList = z.object({ items: z.array(MasterTimeOffRequestView) });
export const CreateEmployeeTimeOffRequest = z
    .object({
    employeeId: Uuid,
    branchId: Uuid,
    dateFrom: LocalDate,
    dateTo: LocalDate,
    /** Пусто = день целиком. Заполнено — отсутствие внутри дня («уйду с 15:00»). */
    startsAt: LocalTime.optional(),
    endsAt: LocalTime.optional(),
    note: z.string().trim().max(500).optional(),
    /** Что делать с уже стоящими в этом периоде записями — молча их бросать нельзя. */
    existingAppointments: z.enum(['block', 'unassign', 'keep']).default('block'),
})
    // Перевёрнутый период раньше проходил и отвечал успехом «оформлено 0 дней» — с аудитом
    // и событием «график изменился», но без единой строки: успех без эффекта хуже отказа.
    .refine((t) => t.dateFrom <= t.dateTo, {
    error: 'период отсутствия заканчивается раньше, чем начинается',
    path: ['dateTo'],
})
    // Отсутствие через полночь внутри одной даты не выражается: «с 15:00 до 10:00» — ошибка
    // ввода, а не ночь. Ночь оформляется двумя датами. Пустое начало — это полночь, поэтому
    // «до 00:00» без начала — тоже пустое окно.
    .refine((t) => t.endsAt === undefined || (t.startsAt ?? '00:00') < t.endsAt, {
    error: 'конец отсутствия внутри дня должен быть позже начала',
    path: ['endsAt'],
});
/**
 * Удержание слота администратором: «клиент подумает и перезвонит, придержи мне 15:00».
 * Своё удержание можно снять, чужое — нельзя: за чужим стоит живой человек, который
 * прямо сейчас вводит код.
 */
export const CreateManualHoldRequest = z.object({
    branchId: Uuid,
    employeeId: Uuid,
    resourceId: Uuid.optional(),
    startAt: Instant,
    endAt: Instant,
    note: z.string().trim().max(300).optional(),
    /** Сервер обрежет по booking_rules.holdTtlSeconds: сутками держать день салона нельзя. */
    ttlSeconds: z.number().int().min(60).max(7200).optional(),
});
export const ReleaseManualHoldRequest = z.object({ holdId: Uuid });
/**
 * «Мастер заболел — переназначить весь день» (GAPS C5). Типовая утренняя операция,
 * а не редкость, и делать её записью за записью — гарантированно потерять одну из двенадцати.
 *
 * Выполняется одной транзакцией: либо переезжают все перечисленные записи, либо ни одна.
 * Пустой toEmployeeId означает «в неразобранное»: иногда заменить некем, и это честный исход.
 */
export const ReassignEmployeeDayRequest = z.object({
    branchId: Uuid,
    date: LocalDate,
    fromEmployeeId: Uuid,
    toEmployeeId: Uuid.nullable(),
    /** Пусто = все записи мастера на эту дату; список — если часть визитов остаётся. */
    appointmentIds: z.array(Uuid).max(200).optional(),
    notifyClients: z.boolean().default(true),
    acknowledgedWarnings: z.array(CabinetWarningCode).default([]),
});
/** Результат массового переназначения: что переехало, что не смогло и почему. */
export const ReassignEmployeeDayResult = z.object({
    movedAppointmentIds: z.array(Uuid),
    conflicts: z.array(z.object({ appointmentId: Uuid, warning: CabinetWarning })),
});
/** Причина отмены из справочника салона — список для диалога отмены. */
export const CancelReasonView = z.object({
    id: Uuid,
    code: z.string(),
    title: z.string(),
    actor: CancellationActor,
    countsAsNoShow: z.boolean(),
    forfeitsPrepay: z.boolean(),
    triggersQuarantine: z.boolean(),
    isSystem: z.boolean(),
    isActive: z.boolean(),
    sortOrder: SortOrder,
});
/** Причины фильтруются по инициатору: у клиента одни, у салона другие. */
export const ListCancelReasonsQuery = z.object({
    actor: CancellationActor.optional(),
    includeInactive: z.boolean().default(false),
});
export const UpsertCancelReasonRequest = z.object({
    id: Uuid.optional(),
    code: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'код причины — латиница в нижнем регистре'),
    title: z.string().trim().min(1).max(200),
    actor: CancellationActor.default('staff'),
    countsAsNoShow: z.boolean().default(false),
    forfeitsPrepay: z.boolean().default(false),
    triggersQuarantine: z.boolean().default(false),
    sortOrder: SortOrder.default(0),
    isActive: z.boolean().default(true),
});
// ───────────────────────── клиенты ─────────────────────────
/**
 * Поиск в базе клиентов.
 *
 * search ищет одновременно по имени и по ЛЮБОМУ из телефонов карточки, от неполного ввода
 * и в любом формате: администратор набирает «9161234567», «8 916…» или четыре последние
 * цифры с бумажки, пока говорит по телефону. Приводит к E.164 сервер — заставлять человека
 * набирать канонический формат в разговоре невозможно.
 */
export const ClientListQuery = PageQuery.extend({
    search: z.string().trim().max(120).optional(),
    tagIds: z.array(Uuid).max(20).optional(),
    /** Только с действующим ограничением онлайн-записи. */
    blacklistedOnly: z.boolean().optional(),
    minNoShowCount: z.number().int().min(1).max(100).optional(),
    /** «Не был больше трёх месяцев» — основа выборки для возврата клиентов. */
    lastVisitBefore: LocalDate.optional(),
    lastVisitEmployeeId: Uuid.optional(),
    sort: z.enum(['last_visit', 'name', 'visits']).default('last_visit'),
    /** Слитые дубли по умолчанию скрыты: это не карточки, а указатели на выжившую. */
    includeMerged: z.boolean().default(false),
});
export const ClientListItem = z.object({
    id: Uuid,
    displayName: z.string(),
    phoneMasked: MaskedPhone.nullable(),
    phone: Phone.nullable(),
    tags: z.array(ClientTagBadge),
    lastVisitAt: Instant.nullable(),
    visitsCount: z.number().int().nonnegative(),
    noShowCount: z.number().int().nonnegative(),
    isBlacklisted: z.boolean(),
});
/**
 * Страница списка. totalCount допускает null: точный счёт по базе в 20 000 карточек
 * с фильтром по тегам — отдельный тяжёлый запрос, и отдавать его на каждую прокрутку
 * дороже, чем он стоит. Когда счёт дёшев, он приходит: администратору он нужен,
 * чтобы понять, что фильтр слишком широкий.
 */
export const ClientListResult = z.object({
    items: z.array(ClientListItem),
    nextCursor: z.string().nullable(),
    totalCount: z.number().int().nonnegative().nullable(),
});
export const ClientPhoneView = z.object({
    id: Uuid,
    phoneMasked: MaskedPhone,
    phone: Phone.nullable(),
    isPrimary: z.boolean(),
    /** Не подтверждённый кодом телефон записан со слов: вход клиенту по нему не открываем. */
    verifiedAt: Instant.nullable(),
});
export const ClientChannelView = z.object({
    id: Uuid,
    channel: z.enum(['telegram', 'max', 'mobile', 'email', 'sms']),
    /** Идентификатор в чужой системе показывается маской: это тоже персональные данные. */
    externalIdMasked: z.string(),
    isVerified: z.boolean(),
    subscribed: z.boolean(),
    lastSeenAt: Instant.nullable(),
});
export const ClientNoteView = z.object({
    id: Uuid,
    body: z.string(),
    isPrivate: z.boolean(),
    isPinned: z.boolean(),
    authorName: z.string().nullable(),
    createdAt: Instant,
    updatedAt: Instant,
});
export const ClientBlacklistEntryView = z.object({
    id: Uuid,
    reason: z.string(),
    blocksOnlineBooking: z.boolean(),
    createdByName: z.string().nullable(),
    createdAt: Instant,
    expiresAt: Instant.nullable(),
    revokedAt: Instant.nullable(),
    revokedByName: z.string().nullable(),
    revokeNote: z.string().nullable(),
});
/**
 * Виды согласий. Список плоский НАМЕРЕННО: согласие на обработку ПД и согласие
 * на рекламу — разные документы с 01.09.2025, и объединять их в один флаг нельзя.
 * Рекламное сообщение без отдельного рекламного согласия — штраф ФАС, платит его
 * салон, а кнопку «разослать» ему дали мы.
 */
export const ClientConsentKind = z.enum([
    'pd',
    'ads',
    'photo',
    'channel_sms',
    'channel_telegram',
    'channel_max',
    'channel_push',
    'channel_email',
]);
/**
 * Как отозвано согласие: сотрудником по требованию клиента, самим клиентом (ссылка
 * отписки или чат), слиянием карточек или обезличиванием. null у отозванного — отзыв
 * старше этого признака, источник неизвестен.
 */
export const ClientConsentRevokeSource = z.enum(['staff', 'client', 'merge', 'erasure']);
export const ClientConsentView = z.object({
    kind: ClientConsentKind,
    version: z.string(),
    grantedAt: Instant,
    revokedAt: Instant.nullable(),
    source: BookingSource.nullable(),
    /**
     * Принято сотрудником без технических полей — доказательство бумажное и лежит у салона
     * (ТЗ 03, 3-А5: «интерфейс обязан это сказать словами»).
     */
    paper: z.boolean(),
    /** Устройство одним словом («iPhone») для электронного согласия; адрес наружу не отдаётся. */
    device: z.string().nullable(),
    /** Кто отметил согласие в кабинете. null — дано самим клиентом или отмечено до признака. */
    grantedByName: z.string().nullable(),
    revokeSource: ClientConsentRevokeSource.nullable(),
    revokedByName: z.string().nullable(),
});
/**
 * Отметка согласия, полученного не через экран: на бумаге или устно при администраторе.
 *
 * ТРИ ОГРАНИЧЕНИЯ ЗАДАНЫ ЗДЕСЬ, А НЕ В ИНТЕРФЕЙСЕ, потому что журнал согласий —
 * доказательство, а доказательство не должно зависеть от того, какая форма его заполняла.
 *
 * 1. ВЕРСИЯ ТЕКСТА ОБЯЗАТЕЛЬНА. Тексты меняются, согласие даётся на конкретную редакцию.
 *    Без версии через год невозможно ответить на единственный вопрос, ради которого
 *    журнал существует: на что именно человек соглашался.
 * 2. ДАТА НЕ БЫВАЕТ БУДУЩЕЙ. Бумагу можно подписать вчера, но не завтра; согласие
 *    с датой вперёд — это разрешение писать человеку до того, как он согласился.
 * 3. ИСТОЧНИК НЕ ПРИНИМАЕТСЯ ОТ КЛИЕНТА ВОВСЕ. Он равен «администратор» по факту
 *    вызова: запрос пришёл из кабинета, от вошедшего сотрудника. Иначе журнал
 *    позволял бы нарисовать «клиент нажал на сайте», и его доказательная сила
 *    исчезла бы целиком (ТЗ 03-клиенты: «нельзя задним числом поставить галочку
 *    без указания, откуда оно взялось»).
 */
export const GrantClientConsentRequest = z.object({
    clientId: Uuid,
    kind: ClientConsentKind,
    version: z.string().trim().min(1).max(32),
    /** Когда человек подписал бумагу. По умолчанию — сейчас. */
    grantedAt: Instant.optional(),
});
/**
 * Отзыв согласия по требованию клиента.
 *
 * СТРОКА НЕ УДАЛЯЕТСЯ И НЕ ПРАВИТСЯ: проставляется revoked_at. Удаление лишило бы
 * салон доказательства, что согласие вообще было, — а доказывать ему придётся обе
 * вещи сразу: и что писал по согласию, и что перестал по отзыву.
 *
 * Отзыв действует НЕМЕДЛЕННО, а не со следующей рассылки: отбор получателей смотрит
 * в ту же таблицу, поэтому закрытым канал становится в тот же миг.
 */
export const RevokeClientConsentRequest = z.object({
    clientId: Uuid,
    kind: ClientConsentKind,
});
/** Журнал согласий клиента после изменения — целиком, чтобы карточка не догадывалась. */
export const ClientConsentsResponse = z.object({
    consents: z.array(ClientConsentView),
    /**
     * Отозвано согласие на обработку ПД. Само по себе это НЕ останавливает обработку
     * и ничего не удаляет: прекращение — отдельное действие с отдельным правом
     * (clients.erase). Флаг существует, чтобы интерфейс сказал это вслух, а не оставил
     * администратора в уверенности, что он всё сделал.
     */
    requiresErasure: z.boolean(),
});
/** Денормализованные счётчики. Пересчитываются целиком из истории, а не инкрементом. */
export const ClientStatsView = z.object({
    visitsCount: z.number().int().nonnegative(),
    noShowCount: z.number().int().nonnegative(),
    cancelledCount: z.number().int().nonnegative(),
    firstVisitAt: Instant.nullable(),
    lastVisitAt: Instant.nullable(),
    /**
     * Сколько человек оставил в салоне. null — деньги скрыты: здесь это ТОЛЬКО право
     * finance.view, потому что сумма складывается из визитов ко всем мастерам, и настройка
     * «показывать мастеру его выработку» такого числа не открывает.
     */
    ltvMinor: MinorAmount.nullable(),
    avgCheckMinor: MinorAmount.nullable(),
});
/**
 * Карточка клиента целиком.
 *
 * Заметки приходят вместе с карточкой, а не отдельным запросом: закреплённая заметка
 * («аллергия на осветлитель») обязана быть перед глазами до процедуры, и лишний запрос —
 * это лишний шанс её не показать. Приватные заметки отфильтрованы сервером по правам,
 * а не помечены флагом для скрытия на клиенте.
 */
export const ClientCardView = z.object({
    id: Uuid,
    displayName: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    birthDate: LocalDate.nullable(),
    gender: z.enum(['female', 'male', 'other', 'unknown']),
    locale: Locale,
    source: BookingSource.nullable(),
    phones: z.array(ClientPhoneView),
    channels: z.array(ClientChannelView),
    tags: z.array(ClientTagBadge),
    notes: z.array(ClientNoteView),
    stats: ClientStatsView,
    /** Действующее ограничение; история ограничений запрашивается отдельно. */
    blacklist: ClientBlacklistEntryView.nullable(),
    consents: z.array(ClientConsentView),
    /** Заполнено у слитой карточки: она не удалена, а указывает на выжившую. */
    mergedIntoId: Uuid.nullable(),
    /** Имя выжившей — для плашки «Эта карточка объединена с «Иванова Мария»». */
    mergedIntoName: z.string().nullable(),
    /**
     * Карточки, слитые В ЭТУ и не разъединённые. Отсюда отмена слияния: ошибку замечают
     * в выжившей карточке («откуда у неё чужие визиты?»), а не в слитой.
     */
    mergedFrom: z.array(z.object({ id: Uuid, displayName: z.string(), mergedAt: Instant })),
    createdAt: Instant,
    updatedAt: Instant,
});
/**
 * Заведение клиента. Телефон обязателен: карточка без телефона не находится в разговоре
 * и не участвует в дедупликации — то есть создаёт дубль при первом же следующем визите.
 */
export const CreateClientRequest = z.object({
    displayName: z.string().trim().min(1).max(200),
    phone: Phone,
    extraPhones: z.array(Phone).max(5).default([]),
    firstName: z.string().trim().max(100).optional(),
    lastName: z.string().trim().max(100).optional(),
    birthDate: LocalDate.optional(),
    gender: z.enum(['female', 'male', 'other', 'unknown']).default('unknown'),
    locale: Locale.optional(),
    tagIds: z.array(Uuid).max(20).default([]),
    consents: AdminConsentCapture,
    /**
     * Подтверждение «да, я знаю, что такой номер уже есть, это другой человек».
     * Запрещать совпадение нельзя: один номер у мамы и ребёнка — обычное дело.
     */
    allowDuplicatePhone: z.boolean().default(false),
});
export const UpdateClientRequest = z.object({
    clientId: Uuid,
    displayName: z.string().trim().min(1).max(200).optional(),
    firstName: z.string().trim().max(100).nullable().optional(),
    lastName: z.string().trim().max(100).nullable().optional(),
    birthDate: LocalDate.nullable().optional(),
    gender: z.enum(['female', 'male', 'other', 'unknown']).optional(),
    locale: Locale.optional(),
    expectedUpdatedAt: ExpectedVersion,
});
/**
 * Телефоны правятся отдельными действиями, а не массивом внутри карточки: присланный
 * целиком массив означает «удалить всё, чего в нём нет», и параллельная правка двух
 * администраторов молча сотрёт один из номеров.
 */
export const AddClientPhoneRequest = z.object({
    clientId: Uuid,
    phone: Phone,
    makePrimary: z.boolean().default(false),
    /**
     * Номер уже есть в другой карточке — то же подтверждение, что при заведении клиента:
     * общий номер у мамы и ребёнка — норма (ТЗ 03, «Создание клиента»), но добавлять его
     * молча нельзя — администратор должен знать, что пара попадёт в «Похожие карточки».
     */
    allowDuplicatePhone: z.boolean().default(false),
});
export const SetPrimaryClientPhoneRequest = z.object({ clientId: Uuid, phoneId: Uuid });
export const RemoveClientPhoneRequest = z.object({ clientId: Uuid, phoneId: Uuid });
export const UpsertClientNoteRequest = z.object({
    clientId: Uuid,
    noteId: Uuid.optional(),
    body: z.string().trim().min(1).max(4000),
    isPrivate: z.boolean().default(false),
    isPinned: z.boolean().default(false),
});
export const SetClientTagsRequest = z.object({
    clientIds: z.array(Uuid).min(1).max(500),
    addTagIds: z.array(Uuid).max(20).default([]),
    removeTagIds: z.array(Uuid).max(20).default([]),
});
/**
 * Внесение в чёрный список САЛОНА.
 *
 * Сквозного списка по платформе нет и не будет: согласие человек давал салону, а не нам,
 * и сообщать другим салонам, что он не пришёл, мы права не имеем (ADR-004). Причина
 * обязательна и попадает человеку на глаза при споре — «просто так» в список не вносят.
 * Срок необязателен, но осмыслен: бессрочный бан за одну неявку переживёт и администратора,
 * который его поставил, и повод, по которому он поставлен.
 */
export const BlacklistClientRequest = z.object({
    clientId: Uuid,
    reason: z.string().trim().min(3).max(1000),
    blocksOnlineBooking: z.boolean().default(true),
    expiresAt: Instant.nullable().default(null),
});
/** Снятие ограничения — это revoke записи, а не её удаление: предыстория нужна следующему. */
export const RevokeClientBlacklistRequest = z.object({
    entryId: Uuid,
    note: z.string().trim().max(1000).optional(),
});
/** Почему система считает две карточки одним человеком. Сила признаков разная. */
export const DuplicateMatchReason = z.enum(['phone', 'name_birthdate', 'name_created_close']);
export const FindClientDuplicatesQuery = PageQuery.extend({
    reason: DuplicateMatchReason.optional(),
});
/**
 * Пара похожих карточек. Совпадение телефона — самый сильный признак и при этом
 * НЕ доказательство: мама и ребёнок на один номер это норма. Поэтому у каждой пары
 * есть действие «это разные люди», иначе список предложений станет вечным шумом
 * и салон перестанет в него заходить.
 */
export const ClientDuplicateGroup = z.object({
    reason: DuplicateMatchReason,
    clients: z
        .array(z.object({
        id: Uuid,
        displayName: z.string(),
        phonesMasked: z.array(MaskedPhone),
        visitsCount: z.number().int().nonnegative(),
        lastVisitAt: Instant.nullable(),
        createdAt: Instant,
        updatedAt: Instant,
    }))
        .min(2),
});
export const ClientDuplicatesResult = z.object({
    items: z.array(ClientDuplicateGroup),
    nextCursor: z.string().nullable(),
    /**
     * Пар по каждому признаку — для чипов «Совпадает телефон · 128» (3-А7). Пара считается
     * один раз, по САМОМУ СИЛЬНОМУ своему признаку: совпавшая и по телефону, и по имени
     * с датой рождения — это пара «по телефону», а не две пары.
     */
    counts: z.object({
        phone: z.number().int().nonnegative(),
        name_birthdate: z.number().int().nonnegative(),
        name_created_close: z.number().int().nonnegative(),
    }),
});
/**
 * Слияние дублей — главный сценарий первой недели после переезда от конкурента.
 *
 * Чего в этой схеме НЕТ и почему: счётчиков визитов, неявок и сумм. Они не выбираются
 * руками, а пересчитываются из объединённой истории. Выбранные вручную значения задвоятся
 * после импорта — это классическая авария переезда, и закрыть её можно только отсутствием
 * самой возможности.
 *
 * Согласия тоже не выбираются: объединяется журнал целиком, а действующим считается то,
 * что даёт МЕНЬШЕ прав — если в одной карточке рекламное согласие отозвано, после слияния
 * оно отозвано. Обратное поведение означало бы рассылку без согласия за счёт салона.
 *
 * Две версии в запросе — не перестраховка: пока администратор выбирал поля, в любую
 * из карточек могли добавить визит или телефон, и решение принималось по устаревшим данным.
 */
export const MergeClientsRequest = z.object({
    survivorId: Uuid,
    mergedId: Uuid,
    /** Поле за полем: чьё значение остаётся. Телефоны, каналы, заметки и метки — всегда все. */
    fieldChoices: z
        .object({
        displayName: z.enum(['survivor', 'merged']).default('survivor'),
        firstName: z.enum(['survivor', 'merged']).default('survivor'),
        lastName: z.enum(['survivor', 'merged']).default('survivor'),
        birthDate: z.enum(['survivor', 'merged']).default('survivor'),
        gender: z.enum(['survivor', 'merged']).default('survivor'),
        locale: z.enum(['survivor', 'merged']).default('survivor'),
    })
        // prefault, а не default: значение подставляется ДО разбора, поэтому умолчания
        // вложенных полей отрабатывают сами. С default пришлось бы перечислять весь объект
        // целиком и держать два списка умолчаний, которые разъедутся при первой же правке.
        .prefault({}),
    /** Какой из объединённых телефонов станет основным. */
    primaryPhoneId: Uuid,
    /**
     * Версии карточек ВМЕСТЕ С ДОЧЕРНИМИ СТРОКАМИ (ClientMergeSide.version), а не
     * clients.updated_at: запись или телефон, добавленные в карточку, пока администратор
     * выбирал поля, меняют её состав, но не её updated_at.
     */
    expectedSurvivorVersion: z.string().min(1).max(64),
    expectedMergedVersion: z.string().min(1).max(64),
});
export const ClientMergePreviewQuery = z.object({ leftId: Uuid, rightId: Uuid });
/**
 * Одна сторона сравнения (3-А7, экран слияния). Ровно то, по чему решают «оставить это
 * или то», и ничего сверх: телефоны — маской (решение принимается по совпадению,
 * а не по номеру), заметки — числом и закреплённой, если она не приватная.
 */
export const ClientMergeSide = z.object({
    id: Uuid,
    displayName: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    birthDate: LocalDate.nullable(),
    gender: z.enum(['female', 'male', 'other', 'unknown']),
    locale: Locale,
    source: BookingSource.nullable(),
    createdAt: Instant,
    phones: z.array(z.object({
        id: Uuid,
        phoneMasked: MaskedPhone,
        isPrimary: z.boolean(),
        /**
         * Тот же номер есть и в другой карточке. При слиянии такой номер не переезжает —
         * он и есть дубль; основным выбрать его можно только у главной карточки.
         */
        alsoInOther: z.boolean(),
    })),
    channels: z.array(z.string()),
    notesCount: z.number().int().nonnegative(),
    pinnedNote: z.string().nullable(),
    tags: z.array(z.string()),
    visitsCount: z.number().int().nonnegative(),
    noShowCount: z.number().int().nonnegative(),
    appointmentsCount: z.number().int().nonnegative(),
    lastVisitAt: Instant.nullable(),
    /** Начало самой недавно СОЗДАННОЙ записи — для «добавили запись на 18 сентября». */
    latestBookedStartAt: Instant.nullable(),
    adsConsent: z.enum(['given', 'revoked', 'never']),
    blacklisted: z.boolean(),
    /** Карточка уже слита с другой — сливать её нельзя, можно открыть выжившую. */
    mergedIntoId: Uuid.nullable(),
    version: z.string(),
});
export const ClientMergePreview = z.object({ left: ClientMergeSide, right: ClientMergeSide });
/**
 * «Объединить все точные совпадения по телефону» (3-А7, «Много данных»).
 *
 * expectedPairs — число, которое администратор видел в подтверждении «Объединить 128 пар?».
 * Если за это время список изменился, массовое действие не выполняется: человек
 * соглашался на 128, а не на 131.
 */
export const MergePhoneMatchesRequest = z.object({
    expectedPairs: z.number().int().positive().max(500),
});
export const MergePhoneMatchesResult = z.object({
    merged: z.number().int().nonnegative(),
    /** Пары, где карточка уже участвовала в этом проходе: тройной дубль сливается по шагам. */
    skipped: z.number().int().nonnegative(),
});
/**
 * Отмена слияния. Существует потому, что слияние обратимо — и это надо прямо обещать
 * администратору: иначе он побоится нажать кнопку, и дубли останутся в базе навсегда.
 */
export const UnmergeClientRequest = z.object({ clientId: Uuid });
/**
 * «Это разные люди» — пара больше не предлагается никогда.
 *
 * Решение попадает в постоянную таблицу `client_duplicate_exclusions` и не имеет срока
 * годности: администратор объясняет системе, что мать и дочь на одном номере — двое,
 * ровно один раз.
 */
export const MarkClientsDistinctRequest = z.object({
    clientIds: z.array(Uuid).length(2),
    /**
     * Почему разные, словами («мать и дочь, один номер»). Необязательно: требовать
     * объяснение на каждое «нет» — значит получить «нет» с полем, набранным пробелом.
     */
    note: z.string().trim().min(1).max(300).optional(),
});
/**
 * Выгрузка данных ОДНОГО клиента по его требованию (152-ФЗ).
 *
 * Это не выгрузка базы: у салона жёсткий срок на ответ субъекту, и антифрод-квота на этот
 * сценарий не распространяется — иначе законное требование человека упрётся в защиту
 * от увода базы. Приватные служебные заметки администрации по умолчанию не входят:
 * основание у них отдельное, и решение по ним принимает салон, а не кнопка.
 */
export const ExportClientDataRequest = z.object({
    clientId: Uuid,
    includePrivateNotes: z.boolean().default(false),
    format: z.enum(['json', 'csv']).default('json'),
});
/**
 * Массовая выгрузка выборки — противоположный сценарий и главный вектор утечки базы
 * (ст. 272.1 УК: не хакер, а свой человек с легальным доступом). Поэтому отдельное право,
 * свежее подтверждение вторым фактором, суточная квота и запрет в первые сутки после смены
 * пароля или входа с нового устройства — типовой картины после угона доступа (GAPS E2).
 */
export const ExportClientsRequest = z.object({
    filter: ClientListQuery.optional(),
    clientIds: z.array(Uuid).max(1000).optional(),
    format: z.enum(['csv', 'json']).default('csv'),
    /** Зачем выгружаем: попадает в аудит и в разбор, если база всплывёт. */
    purpose: z.string().trim().min(3).max(300),
});
/**
 * Задача выгрузки. Всегда асинхронная и всегда со ссылкой ограниченного срока жизни:
 * мгновенная кнопка «скачать всё» — это готовый инструмент выноса базы, а не удобство.
 */
export const ExportJobView = z.object({
    jobId: Uuid,
    status: z.enum(['queued', 'running', 'ready', 'failed', 'expired']),
    requestedAt: Instant,
    readyAt: Instant.nullable(),
    downloadUrl: z.url().nullable(),
    expiresAt: Instant.nullable(),
    rowCount: z.number().int().nonnegative().nullable(),
    /** Остаток суточной квоты карточек — показывается до запуска, а не после отказа. */
    quotaRemaining: z.number().int().nonnegative().nullable(),
});
/**
 * Удаление данных клиента по его требованию. Это НЕ удаление карточки: финансовые
 * и фискальные строки салон обязан хранить по своим основаниям, поэтому ПД
 * обезличиваются, а история остаётся обезличенной (GAPS C11). Решение принимает салон
 * как оператор, мы исполняем и записываем в аудит.
 */
export const EraseClientDataRequest = z.object({
    clientId: Uuid,
    reason: z.string().trim().min(3).max(500),
    /** Осознанное подтверждение необратимости: отменить это действие нельзя. */
    confirmIrreversible: z.literal(true),
});
// ───────────────────────── справочники: услуги и категории ─────────────────────────
export const UpsertServiceCategoryRequest = z.object({
    id: Uuid.optional(),
    parentId: Uuid.nullable().default(null),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).nullable().default(null),
    ord: SortOrder.default(0),
    isActive: z.boolean().default(true),
});
/**
 * Шаг составной услуги. occupiesEmployee=false на шаге выдержки — это и есть пауза,
 * в которую мастер берёт другого клиента; occupiesResource=true рядом обязателен,
 * иначе в занятое кресло посадят второго человека.
 */
export const ServiceStepInput = z.object({
    ord: z.number().int().min(1).max(20),
    name: z.string().trim().min(1).max(200),
    durationMinutes: z.number().int().min(5).max(12 * 60),
    occupiesEmployee: z.boolean().default(true),
    occupiesResource: z.boolean().default(true),
});
/**
 * Создание и правка услуги.
 *
 * Буферы до и после — внутренняя кухня салона и в длительность для клиента не входят:
 * показать «стрижка 50 минут» вместо 30 значит выглядеть дороже и медленнее, чем салон есть.
 */
export const UpsertServiceRequest = z
    .object({
    id: Uuid.optional(),
    /** Пусто = услуга общая для всех филиалов тенанта. */
    branchId: Uuid.nullable().default(null),
    categoryId: Uuid.nullable().default(null),
    kind: z.enum(['simple', 'composite', 'group']).default('simple'),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(4000).nullable().default(null),
    durationMinutes: z.number().int().min(5).max(24 * 60),
    bufferBeforeMinutes: z.number().int().min(0).max(240).default(0),
    bufferAfterMinutes: z.number().int().min(0).max(240).default(0),
    capacity: z.number().int().min(1).max(200).default(1),
    priceMinor: MinorAmount.default(0),
    /** Внутренние услуги («блок под фотосессию») живут в каталоге, но онлайн не продаются. */
    isOnlineBookable: z.boolean().default(true),
    isActive: z.boolean().default(true),
    ord: SortOrder.default(0),
    steps: z.array(ServiceStepInput).max(20).default([]),
    /**
     * Версия услуги, которую видел редактор (updated_at из каталога). Двое правят прайс —
     * второй не затирает первого молча. Необязательна: импорт и первичное заполнение
     * пишут без неё.
     */
    expectedUpdatedAt: Instant.optional(),
    resourceRequirements: z
        .array(z.object({ resourceTypeId: Uuid, qty: z.number().int().min(1).max(10).default(1) }))
        .max(10)
        .default([]),
})
    .refine((s) => s.kind !== 'composite' || s.steps.length >= 2, {
    error: 'у составной услуги должно быть не меньше двух шагов',
    path: ['steps'],
})
    .refine((s) => s.kind === 'composite' || s.steps.length === 0, {
    error: 'шаги допустимы только у составной услуги',
    path: ['steps'],
})
    // ПРАВИЛА СОСТАВНОЙ УСЛУГИ (ТЗ 03, Б3). Они в контракте, а не в экране: шаги приходят
    // и из импорта, и из API, и услуга, начинающаяся с паузы, ломает журнал одинаково.
    // Шаги идут встык и нумеруются подряд: «зазор» между ними — это пауза, а пауза — шаг.
    .refine((s) => s.steps
    .map((step) => step.ord)
    .toSorted((a, b) => a - b)
    .every((ord, index) => ord === index + 1), { error: 'шаги нумеруются подряд с первого: пауза — это тоже шаг, зазоров не бывает', path: ['steps'] })
    .refine((s) => {
    if (s.steps.length === 0)
        return true;
    const ordered = s.steps.toSorted((a, b) => a.ord - b.ord);
    return ordered[0]?.occupiesEmployee === true && ordered.at(-1)?.occupiesEmployee === true;
}, { error: 'первый и последний шаг занимают мастера: услуга не может начинаться или кончаться паузой', path: ['steps'] })
    .refine((s) => s.steps.every((step) => step.occupiesEmployee || step.occupiesResource), {
    error: 'на шаге не занят ни мастер, ни ресурс — такой шаг ничего не занимает',
    path: ['steps'],
})
    .refine((s) => s.steps.reduce((sum, step) => sum + step.durationMinutes, 0) <= 24 * 60, {
    error: 'шаги вместе длиннее суток',
    path: ['steps'],
})
    .refine((s) => s.kind === 'group' || s.capacity === 1, {
    error: 'вместимость больше одного места допустима только у группового занятия',
    path: ['capacity'],
})
    // Обратная сторона того же правила. capacity по умолчанию 1, и групповое занятие
    // без явной вместимости проходило контракт, а в базе упиралось в CHECK
    // services_group_capacity_check — администратор получал «внутреннюю ошибку» ровно
    // в самом частом случае: выбрал «группа» и не заметил поле мест.
    .refine((s) => s.kind !== 'group' || s.capacity >= 2, {
    error: 'у группового занятия должно быть не меньше двух мест: укажите вместимость',
    path: ['capacity'],
})
    // Номер шага уникален в услуге (UNIQUE service_steps_tenant_service_ord_key): два
    // «первых» шага не упорядочить, и какой из них займёт мастера раньше, решил бы случай.
    .refine((s) => new Set(s.steps.map((step) => step.ord)).size === s.steps.length, {
    error: 'номера шагов (ord) не должны повторяться',
    path: ['steps'],
})
    // Один тип ресурса — одно требование с количеством: «два кресла» пишется qty = 2,
    // а не двумя строками. Вторая строка упиралась в первичный ключ требований.
    .refine((s) => new Set(s.resourceRequirements.map((r) => r.resourceTypeId)).size ===
    s.resourceRequirements.length, {
    error: 'тип ресурса указан дважды: объедините требования в одно с нужным количеством (qty)',
    path: ['resourceRequirements'],
});
// ───────────────────────── справочники: мастера ─────────────────────────
export const UpsertPositionRequest = z.object({
    id: Uuid.optional(),
    name: z.string().trim().min(1).max(200),
    ord: SortOrder.default(0),
    isActive: z.boolean().default(true),
});
/** Должность в выборе профиля мастера (3-Б7). */
export const PositionView = z.object({
    id: Uuid,
    name: z.string(),
    isActive: z.boolean(),
});
/**
 * ПУБЛИЧНЫЙ ПРОФИЛЬ МАСТЕРА (3-Б7) — ровно четыре поля, которые уходят на витрину, и
 * показ на ней. Отдельно от UpsertEmployeeRequest, который перезаписывает карточку целиком
 * и без версии: управляющий, правящий описание, не должен заодно затирать тариф, цвет
 * в журнале и услуги, которые в эту минуту меняет администратор. Фото — своим запросом:
 * загрузка файла — другой путь с другими отказами.
 */
export const UpdateEmployeeProfileRequest = z
    .object({
    employeeId: Uuid,
    /** «Аня», а не «Соколова А. С.». Обязательно: без имени мастер не публикуется. */
    publicName: z.string().trim().min(1, 'Укажите имя, которое увидит клиент').max(80),
    positionId: Uuid.nullable(),
    about: z.string().trim().max(2000).nullable(),
    /** Показывать на странице записи. Решение салона: мастер себе его не включает. */
    isOnlineBookable: z.boolean(),
    expectedUpdatedAt: Instant,
});
/**
 * Карточка мастера — это человек В САЛОНЕ, а не учётная запись: мастер может работать
 * без входа в кабинет, его расписание ведёт администратор. Доступ в кабинет выдаётся
 * отдельно, приглашением (см. cabinet-auth.ts) — путать эти два списка нельзя.
 */
export const UpsertEmployeeRequest = z.object({
    id: Uuid.optional(),
    displayName: z.string().trim().min(1).max(200),
    /** Как мастера видит клиент на витрине: «Аня», а не «Иванова А. С.». */
    publicName: z.string().trim().max(200).nullable().default(null),
    positionId: Uuid.nullable().default(null),
    branchDefaultId: Uuid.nullable().default(null),
    /** Цвет колонки журнала: без него день на восемь мастеров нечитаем. */
    color: ColorHex.nullable().default(null),
    avatarUrl: z.url().nullable().default(null),
    about: z.string().trim().max(2000).nullable().default(null),
    /** Администратор — сотрудник, но не исполнитель: к нему нельзя записать. */
    isBookable: z.boolean().default(true),
    /** Отдельно от isBookable: «записывать можно только через администратора» — рабочий режим. */
    isOnlineBookable: z.boolean().default(true),
    /** По этому флагу считается тариф платформы, поэтому он меняется через use-case с лимитом. */
    isBillable: z.boolean().default(true),
    hiredOn: LocalDate.nullable().default(null),
    serviceIds: z.array(Uuid).max(500).optional(),
});
/**
 * Увольнение мастера — АРХИВАЦИЯ, а не удаление (GAPS C5): у него остаются прошлые записи,
 * история и расчёты, и вычеркнуть его из прошлого нельзя. Будущие записи надо куда-то деть
 * прямо в этот момент, иначе они повиснут за архивным мастером и всплывут в день визита.
 */
export const ArchiveEmployeeRequest = z
    .object({
    employeeId: Uuid,
    /** Кому передать будущие записи. Пусто = в «неразобранное». */
    reassignToEmployeeId: Uuid.nullable().default(null),
    notifyClients: z.boolean().default(true),
    reason: z.string().trim().max(300).optional(),
    expectedUpdatedAt: ExpectedVersion,
})
    // «Передать записи самому себе» проходило молча: мастер уходил в архив, а его
    // будущие визиты оставались за ним — ровно то, от чего архивация и защищает.
    .refine((r) => r.reassignToEmployeeId !== r.employeeId, {
    error: 'записи нельзя передать самому увольняемому мастеру: выберите другого или оставьте их неразобранными',
    path: ['reassignToEmployeeId'],
});
/**
 * Персональные условия мастера по услуге. NULL в переопределении означает «берём значение
 * услуги», а не ноль: обнулённая цена выглядит как бесплатная стрижка.
 */
export const UpsertEmployeeServiceRequest = z.object({
    employeeId: Uuid,
    serviceId: Uuid,
    durationOverrideMinutes: z.number().int().min(5).max(24 * 60).nullable().default(null),
    priceOverrideMinor: MinorAmount.nullable().default(null),
    isActive: z.boolean().default(true),
});
// ───────────────────────── справочники: ресурсы ─────────────────────────
export const UpsertResourceTypeRequest = z.object({
    id: Uuid.optional(),
    name: z.string().trim().min(1).max(200),
    isActive: z.boolean().default(true),
});
/**
 * Конкретное кресло, кабинет или аппарат. cleanupMinutes — не «запас на всякий случай»,
 * а санитарное время после клиента: без него следующего сажают в грязное кресло,
 * а в аппаратной косметологии это прямое нарушение требований.
 */
export const UpsertResourceRequest = z.object({
    id: Uuid.optional(),
    branchId: Uuid,
    resourceTypeId: Uuid,
    name: z.string().trim().min(1).max(200),
    cleanupMinutes: z.number().int().min(0).max(240).default(0),
    isActive: z.boolean().default(true),
    ord: SortOrder.default(0),
});
// ───────────────────────── справочники: графики смен ─────────────────────────
/**
 * Один день цикла шаблона смен. Время НАСТЕННОЕ: «смена с 9:00» не привязана ни к дате,
 * ни к зоне, и хранить здесь момент нельзя — при переезде филиала в другую зону
 * весь график уехал бы вместе с ним.
 */
export const ShiftPatternDay = z
    .object({
    work: z.boolean(),
    startsAt: LocalTime.optional(),
    endsAt: LocalTime.optional(),
    breaks: z
        .array(z.object({ startsAt: LocalTime, endsAt: LocalTime }))
        .max(5)
        .default([]),
})
    .refine((d) => !d.work || (d.startsAt !== undefined && d.endsAt !== undefined), {
    error: 'у рабочего дня цикла нужны начало и конец смены',
    path: ['startsAt'],
})
    // Перерыв «15:00–14:00» или вне смены раньше сохранялся и валил развёртку графика
    // уже у всех, кому шаблон назначен, — в момент, когда связать это с правкой шаблона
    // некому. Проверяется здесь, в ТОЙ ЖЕ арифметике, что и развёртка (минуты от начала
    // смены): иначе ночная смена 22:00–06:00 с перерывом в 02:00 была бы «вне смены».
    .refine((d) => !d.work ||
    d.startsAt === undefined ||
    d.endsAt === undefined ||
    d.breaks.every((pause) => breakInShift(d.startsAt ?? '', d.endsAt ?? '', pause) !== null), { error: 'перерыв должен начинаться раньше, чем кончается, и лежать внутри смены', path: ['breaks'] })
    .refine((d) => !d.work || d.startsAt === undefined || d.endsAt === undefined || !breaksOverlap(d), {
    error: 'перерывы одного дня не должны пересекаться',
    path: ['breaks'],
});
const MINUTES_PER_DAY = 24 * 60;
/** Настенное «ЧЧ:ММ» в минуты от полуночи; форму уже проверил LocalTime. */
function minutesOfDay(time) {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
}
/**
 * Перерыв в минутах ОТ НАЧАЛА СМЕНЫ или null, если он в смену не помещается.
 *
 * Отсчёт от начала смены, а не от полуночи — единственный способ сравнивать ночную
 * смену без календаря: у 22:00–06:00 перерыв 02:00–02:30 лежит на 240–270-й минуте
 * смены длиной 480, а перерыв через полночь 23:30–00:30 — на 90–150-й. Конец смены,
 * равный её началу, — это сутки («сутки через трое»), а не пустая смена. Та же функция
 * нужна развёртке графика, чтобы положить перерыв на правильные сутки.
 */
export function breakInShift(shiftStartsAt, shiftEndsAt, pause) {
    const start = minutesOfDay(shiftStartsAt);
    const since = (time) => (minutesOfDay(time) - start + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    const length = since(shiftEndsAt) || MINUTES_PER_DAY;
    const from = since(pause.startsAt);
    // Конец перерыва ровно в начале смены — это конец суточной смены, а не её начало.
    const to = since(pause.endsAt) || MINUTES_PER_DAY;
    return from < to && to <= length ? { from, to } : null;
}
function breaksOverlap(day) {
    const placed = day.breaks
        .map((pause) => breakInShift(day.startsAt ?? '', day.endsAt ?? '', pause))
        .filter((p) => p !== null)
        .sort((a, b) => a.from - b.from);
    return placed.some((p, i) => i > 0 && p.from < (placed[i - 1]?.to ?? 0));
}
/**
 * Шаблон смен: 2/2, 5/2, сутки через трое. Длина массива дней обязана совпадать
 * с длиной цикла — иначе развёртка молча пойдёт по укороченному циклу, и график
 * разъедется на третьей неделе, когда это уже невозможно связать с причиной.
 */
export const UpsertShiftPatternRequest = z
    .object({
    id: Uuid.optional(),
    name: z.string().trim().min(1).max(200),
    cycleDays: z.number().int().min(1).max(31),
    days: z.array(ShiftPatternDay).min(1).max(31),
    isActive: z.boolean().default(true),
})
    .refine((p) => p.days.length === p.cycleDays, {
    error: 'число описанных дней должно совпадать с длиной цикла',
    path: ['days'],
});
/**
 * Назначение шаблона мастеру в филиале.
 *
 * anchorDate обязателен: без точки отсчёта 2/2 неотличим от того же 2/2 со сдвигом на сутки.
 * validTo пустой означает «бессрочно»; перевод и увольнение ЗАКРЫВАЮТ назначение датой,
 * а не удаляют его — прошлые рабочие дни обязаны остаться объяснимыми.
 */
export const AssignShiftPatternRequest = z
    .object({
    id: Uuid.optional(),
    employeeId: Uuid,
    branchId: Uuid,
    patternId: Uuid,
    anchorDate: LocalDate,
    validFrom: LocalDate,
    validTo: LocalDate.nullable().default(null),
})
    // Та же граница стоит CHECK-ом в базе; здесь она — чтобы человек получил «по раньше с»,
    // а не общее «значение не подходит» после попытки записи.
    .refine((a) => a.validTo === null || a.validTo >= a.validFrom, {
    error: 'дата окончания назначения не может быть раньше даты начала',
    path: ['validTo'],
});
/**
 * Ручная правка одного дня поверх шаблона («сегодня Аня выйдет на два часа раньше»).
 * Ложится отдельной строкой источника override и не трогает шаблон: иначе правка одного
 * дня переписала бы график всем, кому шаблон назначен.
 */
export const UpsertWorkdayRequest = z.object({
    employeeId: Uuid,
    branchId: Uuid,
    workDate: LocalDate,
    startsAt: LocalTime,
    endsAt: LocalTime,
    note: z.string().trim().max(300).optional(),
    /** Что делать с записями, выпадающими из новых границ смены. */
    existingAppointments: z.enum(['block', 'unassign', 'keep']).default('block'),
});
export const RemoveWorkdayRequest = z.object({
    employeeId: Uuid,
    branchId: Uuid,
    workDate: LocalDate,
    existingAppointments: z.enum(['block', 'unassign']).default('block'),
});
// ───────────────────────── справочники: цены ─────────────────────────
/**
 * Правило динамической цены: «утро вторника −20 %», «выходные дороже».
 *
 * weekdayMask — битовая маска ISO-8601 (бит 0 = понедельник, 127 = все дни): так же,
 * как в базе, чтобы фильтр считался в SQL, а не перебором в приложении.
 * Окно времени — НАСТЕННОЕ время филиала: «счастливые часы с 10 до 12» означают 10:00
 * по месту в любом часовом поясе, и абсолютное время здесь неприменимо в принципе.
 */
export const UpsertPriceRuleRequest = z
    .object({
    id: Uuid.optional(),
    /**
     * Имя, написанное человеком: «Утро вторника −20 %». Обязательно (ТЗ Б10): через полгода
     * никто не вспомнит, что делает «Правило № 3».
     */
    name: z.string().trim().min(1, 'назовите правило словами: «Утро вторника −20 %»').max(200),
    scope: z.enum(['service', 'category', 'employee', 'branch']),
    targetId: Uuid,
    // Ноль — правило, которое не применится никогда; в базе это CHECK weekday_mask ≥ 1.
    weekdayMask: z.number().int().min(1, 'выберите хотя бы один день недели').max(127).default(127),
    timeFrom: LocalTime.nullable().default(null),
    timeTo: LocalTime.nullable().default(null),
    dateFrom: LocalDate.nullable().default(null),
    dateTo: LocalDate.nullable().default(null),
    mode: z.enum(['percent', 'fixed']),
    /** Знаковый процент: −20 это скидка, +15 — наценка. */
    percent: z.number().int().min(-100).max(500).nullable().default(null),
    fixedPriceMinor: MinorAmount.nullable().default(null),
    /** Больше — важнее; при равенстве побеждает более узкая область действия. */
    priority: SortOrder.default(0),
    isActive: z.boolean().default(true),
})
    .refine((r) => (r.mode === 'percent' ? r.percent !== null : r.fixedPriceMinor !== null), {
    error: 'заполните значение, соответствующее выбранному режиму правила',
    path: ['mode'],
})
    // Ровно одно значение — то, что соответствует режиму (CHECK price_rules_mode_value_check).
    // Правило «−10 % и заодно 500 ₽» неоднозначно: какое из двух применится, решал бы
    // порядок условий в расчёте цены, а не салон.
    .refine((r) => (r.mode === 'percent' ? r.fixedPriceMinor === null : r.percent === null), {
    error: 'у правила одно значение: процент для режима percent или цена для режима fixed — второе поле оставьте пустым',
    path: ['mode'],
})
    .refine((r) => (r.timeFrom === null) === (r.timeTo === null), {
    error: 'окно времени задаётся обеими границами или не задаётся вовсе',
    path: ['timeTo'],
})
    // Время настенное и сравнивается строкой «ЧЧ:ММ» — формат фиксирован LocalTime.
    // Окно через полночь (22:00–02:00) одним правилом не выражается: в базе
    // time_from < time_to, и такое правило раньше доходило до CHECK и отвечало 500.
    .refine((r) => r.timeFrom === null || r.timeTo === null || r.timeFrom < r.timeTo, {
    error: 'начало окна времени должно быть раньше конца; ночное окно через полночь задайте двумя правилами',
    path: ['timeTo'],
})
    .refine((r) => r.dateFrom === null || r.dateTo === null || r.dateFrom <= r.dateTo, {
    error: 'дата начала действия правила позже даты окончания',
    path: ['dateTo'],
});
/** Ответ на сохранение правила: «у 12 уже записанных клиентов цена останется прежней». */
export const UpsertPriceRuleResult = z.object({
    id: Uuid,
    /** Будущие записи, на которые правило пришлось бы по времени и цели: их цена не меняется. */
    futureAppointments: z.number().int().nonnegative(),
});
/** Правило, давшее цену, — для подписи ячейки и перехода к нему (3-Б11). */
export const PriceRuleRef = z.object({ id: Uuid, name: z.string().nullable() });
/**
 * Карта цен на неделю (3-Б11): «дни × часы работы филиала» для услуги. Зеркало, не редактор —
 * из ячейки можно только перейти к правилу, которое её определило.
 */
export const PriceMapQuery = z.object({
    serviceId: Uuid,
    branchId: Uuid,
    employeeId: Uuid.optional(),
    /** Понедельник недели (настенная дата филиала). */
    weekStart: LocalDate,
});
export const PriceMapCell = z.object({
    /** «10:00» — настенное время филиала. */
    time: z.string(),
    /**
     * null — филиал закрыт или мастер не работает: цены нет, и это не «0 ₽».
     */
    priceMinor: z.number().int().nonnegative().nullable(),
    rule: PriceRuleRef.nullable(),
});
export const PriceMapView = z.object({
    serviceName: z.string(),
    /** Цена до правил: прайс или цена выбранного мастера. */
    baseMinor: z.number().int().nonnegative(),
    timezone: z.string(),
    hours: z.array(z.string()),
    days: z.array(z.object({ date: LocalDate, cells: z.array(PriceMapCell) })),
    /** Действующих правил в салоне вообще нет — «ничего не сломано, услуга стоит как в прайсе». */
    noRules: z.boolean(),
});
/** Точечная проверка: «услуга / мастер / момент → итоговая цена и объяснение». */
export const PriceCheckQuery = z.object({
    serviceId: Uuid,
    branchId: Uuid,
    employeeId: Uuid.optional(),
    at: Instant,
});
export const PriceCheckView = z.object({
    baseMinor: z.number().int().nonnegative(),
    priceMinor: z.number().int().nonnegative(),
    /** Правило, которое сработало; null — цена как в прайсе. */
    rule: PriceRuleRef.nullable(),
    /**
     * Правила, которые тоже подходят, но проиграли по приоритету или узости: «здесь также
     * действует «Будни −10 %»; сработает «Утро вторника −20 %»» (ТЗ Б10, пересечения).
     */
    alsoMatching: z.array(PriceRuleRef),
});
// ───────────────────────── справочники: филиал, часы, правила записи ─────────────────────────
/**
 * Филиал. Зона — IANA-имя, и она же владелец всего времени в этом филиале (ADR-007 п.4).
 * Числового смещения здесь нет и быть не может: регионы РФ меняли зону в 2016, 2018
 * и в декабре 2020, и отчёт за прошлый период на смещении уедет ровно на час.
 */
/**
 * Филиал в настройках — ТИПИЗИРОВАННЫЙ ответ (4-Б1).
 *
 * До этого `GET /cabinet/branches` возвращал `unknown[]`, и экран собирал поля наугад:
 * упустив одно, он молча стирал его при сохранении — упорный случай для координат
 * и телефона, которые редактируются раз в жизни и потому всегда «не те, что на экране».
 *
 * updatedAt здесь ради того же, ради чего он есть у записи: два человека правят один
 * филиал (владелец из дома, управляющий с планшета), и «последний победил» стирает
 * чужую работу молча.
 */
export const BranchHoursRow = z.object({
    weekday: z.number().int().min(0).max(6),
    opensAt: LocalTime,
    closesAt: LocalTime,
});
export const BranchSettingsView = z.object({
    id: Uuid,
    organizationId: Uuid.nullable(),
    name: z.string(),
    timezone: TimeZone,
    slotStepMinutes: z.number().int().positive(),
    packingMode: z.enum(['grid', 'tight']),
    address: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    phoneE164: Phone.nullable(),
    isActive: z.boolean(),
    updatedAt: Instant,
    hours: z.array(BranchHoursRow),
});
/**
 * Сколько будущих визитов окажется ВНЕ новой сетки, если сменить шаг (4-Б1).
 *
 * ЗАЧЕМ. Шаг 15 → 30 не двигает записи: визит на 14:15 остаётся на 14:15 и рисуется
 * между линиями. Это нормально, но выглядит поломкой, и владелец узнаёт об этом уже
 * после сохранения, на глазах у администратора. Число заранее превращает сюрприз
 * в решение: «12 записей останутся как есть — это ожидаемо?».
 *
 * Минута считается В ЗОНЕ ФИЛИАЛА: сетка рисуется по его времени, а не по UTC.
 */
export const BranchGridPreview = z.object({
    slotStepMinutes: z.number().int().positive(),
    /** Будущие pending и confirmed, чья минута начала не делится на новый шаг. */
    offGrid: z.number().int().nonnegative(),
    /**
     * Ближайшие из них — их и называют вслух: «14:15 у Ани, 16:45 у Кати».
     *
     * ЧЕГО ЗДЕСЬ НЕТ. Имени клиента. Предпросмотр отвечает на вопрос про сетку, и для
     * ответа хватает времени и мастера; персональные данные в подсказке о настройке —
     * это открытая база там, где её никто не спрашивал.
     */
    examples: z.array(z.object({
        appointmentId: Uuid,
        startAt: Instant,
        employeeName: z.string().nullable(),
    })),
});
export const UpsertBranchRequest = z.object({
    id: Uuid.optional(),
    organizationId: Uuid.optional(),
    name: z.string().trim().min(1).max(200),
    timezone: TimeZone,
    address: z.string().trim().max(500).nullable().default(null),
    latitude: z.number().min(-90).max(90).nullable().default(null),
    longitude: z.number().min(-180).max(180).nullable().default(null),
    phoneE164: Phone.nullable().default(null),
    /** Шаг сетки: допустимые значения ограничены и в базе (5, 10, 15, 20, 30, 60). */
    slotStepMinutes: z.union([
        z.literal(5),
        z.literal(10),
        z.literal(15),
        z.literal(20),
        z.literal(30),
        z.literal(60),
    ]),
    packingMode: z.enum(['grid', 'tight']).default('grid'),
    /*
     * Минимального лага и горизонта записи здесь нет намеренно: они живут
     * в правилах записи (UpdateBookingRulesRequest), и это единственное место,
     * откуда их читает витрина. Пока они были и тут, и там, владелец правил
     * горизонт в настройках филиала, а на витрине ничего не менялось —
     * настройка молчала, и понять это можно было только чтением кода.
     */
    /**
     * Версия строки филиала. Обязательна при правке: два человека правят один филиал
     * (владелец из дома, управляющий с планшета), и «последний победил» стирает чужую
     * работу молча.
     *
     * null допустим ТОЛЬКО при создании — строки ещё нет. Прислать null существующему
     * филиалу можно, но это уже не создание, а потеря версии на клиенте, и сервер
     * ответит конфликтом: писать поверх чужой правки вслепую нельзя.
     */
    expectedUpdatedAt: ExpectedVersion.nullable().default(null),
    /** Выключенный филиал исчезает с витрины, но его записи и история остаются. */
    isActive: z.boolean().default(true),
});
/**
 * Часы работы филиала целиком, одним списком.
 *
 * Полная замена, а не правка по одной строке: график — это единый объект, и частичные
 * правки порождают либо дыры («вторник исчез»), либо наложения, которые расчёт слотов
 * молча превратит в пустой день. Несколько строк на один день недели — это легальный
 * перерыв на обед (9:00–13:00 и 14:00–20:00), а не противоречие.
 */
export const UpdateBranchHoursRequest = z.object({
    branchId: Uuid,
    hours: z
        .array(z
        .object({
        // 0 = воскресенье, как EXTRACT(DOW) в PostgreSQL: своей нумерации не заводим.
        weekday: z.number().int().min(0).max(6),
        opensAt: LocalTime,
        closesAt: LocalTime,
    })
        .refine((h) => h.opensAt < h.closesAt, {
        error: 'конец рабочего интервала должен быть позже начала',
        path: ['closesAt'],
    }))
        .max(28),
});
/**
 * Исключение на конкретную дату: праздник, ремонт, сокращённый день 31 декабря.
 * Сюда материализуется решение салона по производственному календарю — сам календарь
 * остаётся подсказкой (1 января барбершоп скорее закрыт, а 8 марта работает вдвое).
 * reason виден клиенту на витрине, и салон должен понимать, что пишет для посторонних.
 */
export const UpsertBranchHoursExceptionRequest = z
    .object({
    branchId: Uuid,
    onDate: LocalDate,
    isClosed: z.boolean().default(false),
    opensAt: LocalTime.nullable().default(null),
    closesAt: LocalTime.nullable().default(null),
    reason: z.string().trim().max(300).nullable().default(null),
})
    .refine((e) => e.isClosed || (e.opensAt !== null && e.closesAt !== null), {
    error: 'если филиал работает, нужны часы работы на эту дату',
    path: ['opensAt'],
})
    // Как у обычных часов работы (UpdateBranchHoursRequest): работу через полночь модель
    // не выражает, и «с 18:00 до 10:00» — это ошибка ввода, которую иначе ловил бы CHECK.
    .refine((e) => e.isClosed || e.opensAt === null || e.closesAt === null || e.opensAt < e.closesAt, {
    error: 'конец рабочего интервала должен быть позже начала',
    path: ['closesAt'],
});
export const DeleteBranchHoursExceptionRequest = z.object({ branchId: Uuid, onDate: LocalDate });
/**
 * Правила онлайн-записи и отмены.
 *
 * branchId пустой = значение по умолчанию для всего салона, строка филиала его перекрывает.
 * Без этих настроек подписанная ссылка «Отменить» позволит клиенту отменить за пять минут
 * до визита, и убыток салон предъявит нам (GAPS C1).
 */
export const UpdateBookingRulesRequest = z.object({
    branchId: Uuid.nullable().default(null),
    /**
     * Версия строки, которую правим. null — строки ещё нет (первая настройка филиала или
     * салона). Без неё две открытые вкладки настроек молча затирали друг друга: правила
     * записи — семь взаимосвязанных чисел, и «последний победил» здесь означает, например,
     * горизонт 60 при сроке отмены 180, о котором никто не просил.
     */
    expectedUpdatedAt: Instant.nullable().default(null),
    minLeadMinutes: z.number().int().min(0).max(30 * 24 * 60).optional(),
    maxHorizonDays: z.number().int().min(1).max(365).optional(),
    cancelDeadlineMinutes: z.number().int().min(0).max(30 * 24 * 60).optional(),
    rescheduleDeadlineMinutes: z.number().int().min(0).max(30 * 24 * 60).optional(),
    /** Отменённое впритык время не возвращается в онлайн-запись эти минуты (GAPS C2). */
    quarantineMinutes: z.number().int().min(0).max(24 * 60).optional(),
    prepayPercent: z.number().int().min(0).max(100).optional(),
    noShowPolicy: z.enum(['none', 'warn', 'require_prepay', 'admin_only', 'block']).optional(),
    noShowThreshold: z.number().int().min(1).max(50).optional(),
    maxActiveAppointments: z.number().int().min(1).max(50).optional(),
    /** Сколько живёт удержание на время оформления: меньше минуты клиент не успеет ввести код. */
    holdTtlSeconds: z.number().int().min(60).max(3600).optional(),
    waitlistHoldSeconds: z.number().int().min(60).max(86400).optional(),
    allowOnlineCancel: z.boolean().optional(),
    allowOnlineReschedule: z.boolean().optional(),
});
/**
 * Что мастеру видно в кабинете — настройка владельца салона (решения владельца 45 и 46).
 *
 * ПОЧЕМУ ЭТО НАСТРОЙКА, А НЕ ПРАВИЛО ПЛАТФОРМЫ. В барбершопе на три кресла мастер сам
 * звонит клиенту и сам считает свою выработку; в сети мастер — самый вероятный будущий
 * сотрудник конкурента, и база уходит через легальный доступ (ADR-024). Одного значения,
 * верного для обоих, не существует, поэтому выбирает владелец.
 *
 * ОБА ФЛАГА ВЫКЛЮЧЕНЫ ПО УМОЛЧАНИЮ: салон, который эту настройку никогда не открывал,
 * получает закрытый вариант. Умолчание в сторону утечки решало бы за владельца.
 *
 * Права `clients.phone.view` и `finance.view` СИЛЬНЕЕ настройки: у кого они есть,
 * тот видит телефоны и деньги независимо от неё. Настройка существует для тех, у кого их нет.
 */
export const StaffVisibilityView = z.object({
    /** Полный телефон клиента мастеру — в журнале и в карточке его собственного визита. */
    showClientPhoneToMasters: z.boolean(),
    /** Деньги мастеру: цена услуги и сумма СВОИХ визитов (своя выработка). */
    showMoneyToMasters: z.boolean(),
    updatedAt: Instant.nullable(),
});
/** Правка настройки. Оба поля обязательны: «оставить как было» — это текущее значение. */
export const UpdateStaffVisibilityRequest = z.object({
    showClientPhoneToMasters: z.boolean(),
    showMoneyToMasters: z.boolean(),
    /**
     * Версия настройки. Две открытые вкладки настроек иначе затирают друг друга молча,
     * а здесь цена молчания — открытая база клиентов: владелец выключил показ телефона,
     * управляющий из своей вкладки вернул его обратно, не зная об этом.
     *
     * null — строки настройки ещё нет: салон не менял умолчаний ни разу.
     */
    expectedUpdatedAt: ExpectedVersion.nullable().default(null),
});
/**
 * ЖУРНАЛ ОТКРЫТИЙ ТЕЛЕФОНА — сводка за неделю (4-Б12).
 *
 * ЗАЧЕМ. Настройка «мастер видит телефон» продаётся салону как доверие, а проверяется
 * только цифрами: кто открывал номера, сколько раз и скольких РАЗНЫХ клиентов. Двадцать
 * открытий одного клиента за день — работа; двадцать разных клиентов подряд — перебор
 * базы глазами (GAPS E3), и увидеть разницу можно только так.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Самих номеров и имён клиентов: это была бы вторая копия персональных
 * данных с другим кругом доступа. Считаются только числа и имена сотрудников салона.
 */
export const PhoneRevealSummaryRow = z.object({
    memberId: Uuid.nullable(),
    displayName: z.string(),
    /** Сколько раз открывал номера за период. */
    reveals: z.number().int().nonnegative(),
    /** Скольких РАЗНЫХ клиентов. Разница с reveals и есть предмет разговора. */
    clients: z.number().int().nonnegative(),
    lastAt: Instant.nullable(),
});
export const PhoneRevealSummary = z.object({
    /** Глубина сводки в днях — её называет экран, чтобы число было с чем сравнивать. */
    days: z.number().int().positive(),
    rows: z.array(PhoneRevealSummaryRow),
});
/** Над чем совершено действие. Перечень общий с базой (audit_target_type). */
export const AuditTargetType = z.enum([
    'client', 'employee', 'member', 'service', 'branch', 'export', 'schedule', 'session', 'tenant',
    'appointment', 'price_rule', 'integration',
]);
/**
 * Чем кончилось действие. Отказы показываются наравне с удачами: «двести отказов
 * по правам за час» — картина перебора, но видна она только тогда, когда неудачные
 * попытки тоже попадают в ленту.
 */
export const AuditResult = z.enum(['success', 'denied', 'failed']);
// ───────────────────────── журнал действий ─────────────────────────
/**
 * Запрос к журналу действий салона.
 *
 * ОКНО ВРЕМЕНИ ОБЯЗАТЕЛЬНО, и это не придирка к удобству. Таблица партиционирована
 * по месяцам с ретеншном в годы; запрос без границ обошёл бы все партиции сразу,
 * а индекс начинается с (tenant_id, occurred_at DESC) — то есть без даты он не
 * отсекает ничего. Экран, который «просто показывает всё», перестанет открываться
 * на втором году работы салона, причём у самого крупного.
 */
export const CabinetAuditQuery = PageQuery.extend({
    from: Instant,
    to: Instant,
    /** Префикс действия: `clients.` покажет всё о клиентах, `clients.export` — только выгрузки. */
    actionPrefix: z.string().trim().max(100).optional(),
    actorUserId: Uuid.optional(),
    targetType: AuditTargetType.optional(),
    targetId: Uuid.optional(),
    result: AuditResult.optional(),
    /** Только действия нашей поддержки: отдельный вопрос и отдельная строка отчёта. */
    supportOnly: z.boolean().optional(),
});
export const CabinetAuditEntry = z.object({
    id: Uuid,
    occurredAt: Instant,
    action: z.string(),
    result: AuditResult,
    targetType: AuditTargetType,
    targetId: Uuid.nullable(),
    /** Имя снимком на момент действия: человек мог уволиться или сменить фамилию. */
    actorDisplayName: z.string(),
    actorUserId: Uuid.nullable(),
    /**
     * Действие совершил не сотрудник салона, а наша поддержка. Флагом, а не
     * идентификатором сеанса: салону нужен факт, а не наш внутренний номер. Показывать
     * этот факт обязательно — требование 152-ФЗ, а не вежливость.
     */
    bySupport: z.boolean(),
    reason: z.string().nullable(),
    details: z.record(z.string(), z.unknown()),
    ip: z.string().nullable(),
});
export const CabinetAuditResult = z.object({
    items: z.array(CabinetAuditEntry),
    nextCursor: z.string().nullable(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * 5-В1 «Сводка владельца», 5-В2 «Разбор: загрузка», 5-В3 «Разбор: отмены»
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Состояние одной карточки сводки.
 *
 * КАЖДАЯ СЧИТАЕТСЯ И ОТКАЗЫВАЕТ ОТДЕЛЬНО. Сводка — шесть независимых расчётов по разным
 * таблицам, и падение одного не имеет права уносить остальные пять: владелец, открывший
 * экран между двумя клиентами, должен увидеть то, что посчиталось, и честное «не
 * посчиталось — повторить» вместо общего экрана ошибки. Ноль вместо отказа запрещён:
 * «выручка 0 ₽» читается как разорение и кончается звонком в поддержку.
 */
export const SummaryCardStatus = z.enum(['ready', 'failed']);
/** Разбивка по точкам — нужна только в режиме «Все филиалы». */
export const SummaryBranchCount = z.object({
    branchId: Uuid,
    branchName: z.string(),
    count: z.number().int().nonnegative(),
});
/**
 * Деньги по точкам.
 *
 * ОТДЕЛЬНЫЙ ТИП, А НЕ SummaryBranchCount С ПОЛЕМ `count`. Копейки, положенные в поле
 * с названием «количество», — это ошибка, которая доживает до экрана: следующий, кто
 * напишет разбивку сети, честно покажет «148 300 записей» вместо «1 483 ₽».
 */
export const SummaryBranchMoney = z.object({
    branchId: Uuid,
    branchName: z.string(),
    minor: z.number().int().nonnegative(),
});
/**
 * «Записей на сегодня» и «Записей на завтра» (5-В1).
 *
 * СВОБОДНЫЕ ЧАСЫ — НЕ УКРАШЕНИЕ, А ГЛАВНОЕ ЧИСЛО ЗАВТРАШНЕЙ КАРТОЧКИ: «ещё можно
 * продать» — это то, ради чего владелец открывает сводку вечером.
 */
export const SummaryBookingsCard = z.object({
    status: SummaryCardStatus,
    /** null означает «не посчиталось», а не «ноль». */
    count: z.number().int().nonnegative().nullable(),
    /** Пришли: отмечен приход (in_progress или completed). */
    arrived: z.number().int().nonnegative().nullable(),
    /** Ждут подтверждения. */
    pending: z.number().int().nonnegative().nullable(),
    /** Из них записались сами — через витрину или бота, а не администратором. */
    online: z.number().int().nonnegative().nullable(),
    /** Свободное время смен в минутах. */
    freeMinutes: z.number().int().nonnegative().nullable(),
    /** Занятое время смен в минутах — чтобы сказать «свободного больше, чем занятого». */
    busyMinutes: z.number().int().nonnegative().nullable(),
    perBranch: z.array(SummaryBranchCount),
});
/** Строка разбора 5-В2 — один мастер. */
export const SummaryLoadRow = z.object({
    employeeId: Uuid,
    displayName: z.string(),
    /** Должность одним словом: «барбер», «колорист». null — не заполнена. */
    position: z.string().nullable(),
    /** Занято услугами, минуты. Только kind='service' и только постоянная занятость. */
    busyMinutes: z.number().int().nonnegative(),
    /** Рабочее время смены, минуты. */
    workMinutes: z.number().int().nonnegative(),
    percent: z.number().int().nonnegative(),
    branchId: Uuid,
    branchName: z.string(),
});
/**
 * «Загрузка мастеров» (5-В1) и её разбор (5-В2).
 *
 * СЧИТАЕТСЯ ПО ЗАНЯТОСТИ, А НЕ ПО ЗАПИСЯМ: `employee_busy` с kind='service' — это то,
 * что действительно занимает мастера, включая шаги групповых занятий. Удержания слотов
 * (expires_at IS NOT NULL) не в счёт: это ещё не работа, а намерение клиента.
 */
export const SummaryLoadCard = z.object({
    status: SummaryCardStatus,
    percent: z.number().int().nonnegative().nullable(),
    /** Разница с тем же днём недели неделей раньше, пункты. null — не с чем сравнивать. */
    deltaPercent: z.number().int().nullable(),
    rows: z.array(SummaryLoadRow),
});
/**
 * «Выручка за сегодня» (5-В1).
 *
 * ПОДПИСЬ ОБЯЗАТЕЛЬНА: «по завершённым записям, не по кассе». Это не одно и то же,
 * и владелец, сверивший сводку с кассовой лентой, обязан заранее знать почему.
 */
export const SummaryRevenueCard = z.object({
    status: SummaryCardStatus,
    minor: z.number().int().nonnegative().nullable(),
    /** Тот же день недели неделей раньше. */
    previousMinor: z.number().int().nonnegative().nullable(),
    deltaPercent: z.number().int().nullable(),
    perBranch: z.array(SummaryBranchMoney),
});
/** Строка разбора 5-В3 — одна потеря. */
export const SummaryLossRow = z.object({
    appointmentId: Uuid,
    startAt: Instant,
    clientName: z.string(),
    employeeName: z.string().nullable(),
    serviceNames: z.array(z.string()),
    /** null — цену видит только тот, у кого есть finance.view. */
    priceMinor: z.number().int().nonnegative().nullable(),
    /** Неявка и отмена — РАЗНЫЕ вещи, и счётчики ведутся отдельно. */
    kind: z.enum(['no_show', 'cancelled']),
    /** Кто отменил. null у неявки. */
    cancelledBy: z.enum(['client', 'staff', 'system']).nullable(),
    reason: z.string().nullable(),
    /** Которая это неявка у клиента: «2-я неявка клиента» или «первая». */
    clientNoShowCount: z.number().int().nonnegative(),
    /** Освобождённое время держится в карантине до этой минуты. */
    quarantineUntil: Instant.nullable(),
});
/** «Отмены и неявки» (5-В1) и их разбор (5-В3). */
export const SummaryLossesCard = z.object({
    status: SummaryCardStatus,
    count: z.number().int().nonnegative().nullable(),
    noShowCount: z.number().int().nonnegative().nullable(),
    cancelledByClient: z.number().int().nonnegative().nullable(),
    cancelledByStaff: z.number().int().nonnegative().nullable(),
    /** Сгоревшее время, минуты. */
    lostMinutes: z.number().int().nonnegative().nullable(),
    rows: z.array(SummaryLossRow),
});
/** Одно свободное окно ближайших часов. */
export const SummaryFreeSlot = z.object({
    employeeId: Uuid,
    employeeName: z.string(),
    startAt: Instant,
    minutes: z.number().int().positive(),
    branchId: Uuid,
    branchName: z.string(),
});
/** «Свободные окна · ближайшие 3 часа» (5-В1) — то, что можно продать прямо сейчас. */
export const SummaryFreeSlotsCard = z.object({
    status: SummaryCardStatus,
    count: z.number().int().nonnegative().nullable(),
    slots: z.array(SummaryFreeSlot),
});
/** Область, по которой посчитана сводка. */
export const SummaryScope = z.object({
    branches: z.array(z.object({ id: Uuid, name: z.string() })),
    /** Человек смотрит всю свою область, а не одну точку. */
    allBranches: z.boolean(),
    /**
     * Зона, в которой определён день.
     *
     * У сети филиалов зоны разные, и «сегодня» у них начинается в разное время. Сводка
     * считает день в зоне ОДНОГО филиала, когда он выбран, и в зоне первого по алфавиту,
     * когда выбраны все, — и честно называет её, чтобы расхождение с журналом филиала
     * в другом поясе можно было объяснить, а не искать.
     */
    timezone: TimeZone,
});
/**
 * Ответ сводки владельца.
 *
 * `mode: 'booking_only'` — салон переведён в режим бережного расхода ресурсов: отчёты
 * выключены, запись и журнал работают. Поле заведено сейчас, потому что экран обязан
 * уметь это показать словами («система бережёт ресурсы для записи клиентов»), а не
 * пустыми карточками.
 */
export const CabinetSummary = z.object({
    /** Минута, на которую посчитаны цифры: «Данные на 14:20» в офлайне. */
    generatedAt: Instant,
    /**
     * Была ли у салона хоть одна запись за всё время.
     *
     * ПОЧЕМУ ЭТО ПРИХОДИТ С СЕРВЕРА, А НЕ ВЫВОДИТСЯ ИЗ НУЛЕЙ. Экран выводил «салон
     * работает первый день» из «всё по нулям», и обычный выходной работающего три года
     * салона выглядел как день открытия: записей нет, смен нет, выручка ноль. Ответ
     * на этот вопрос знает база. null — не посчиталось: тогда экран не утверждает
     * ни того, ни другого.
     */
    everWorked: z.boolean().nullable(),
    /** День, за который посчитано, в зоне области. */
    date: LocalDate,
    scope: SummaryScope,
    mode: z.enum(['full', 'booking_only']),
    today: SummaryBookingsCard,
    tomorrow: SummaryBookingsCard,
    load: SummaryLoadCard,
    revenue: SummaryRevenueCard,
    losses: SummaryLossesCard,
    freeSlots: SummaryFreeSlotsCard,
});
export const CabinetSummaryQuery = z.object({
    /** День сводки. По умолчанию — сегодня в зоне области. */
    date: LocalDate.optional(),
    /** Одна точка. Без него — вся доступная человеку область. */
    branchId: Uuid.optional(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * 5-В4 «Записано» — сколько денег стоит в будущих записях
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Один день периода.
 *
 * ЗАПОЛНЕННОСТЬ — ЭТО НЕ ТО ЖЕ, ЧТО ДЕНЬГИ. День может быть забит дешёвыми услугами
 * и наоборот; владелец смотрит на обе цифры сразу и решает, чем догружать.
 */
export const SummaryBookedDay = z.object({
    date: LocalDate,
    /** Сумма по ценам уже созданных записей: pending и confirmed. */
    minor: z.number().int().nonnegative(),
    count: z.number().int().nonnegative(),
    workMinutes: z.number().int().nonnegative(),
    busyMinutes: z.number().int().nonnegative(),
    percent: z.number().int().nonnegative(),
    /**
     * День за горизонтом ОНЛАЙН-записи.
     *
     * ЭТО НЕ «ЗАПИСЕЙ НЕТ». Горизонт ограничивает витрину и ботов; администратор
     * записывает вручную на любую дату, и записи в таком дне бывают. Признак говорит
     * ровно одно: сам клиент сюда записаться не мог — значит пустота здесь не провал
     * продаж, а настройка филиала.
     */
    beyondHorizon: z.boolean(),
});
/**
 * Сравнение с прошлой неделей — ПРИБЛИЖЁННОЕ, и это сказано полем.
 *
 * Истории статусов и переносов в базе нет: «сколько было записано неделю назад»
 * восстанавливается по времени создания записи и времени отмены — то есть считается,
 * что запись, созданная позже, неделю назад не существовала, а отменённая позже —
 * существовала. Перенос в этой картине выглядит как запись, которая всегда была
 * на новом месте. Показывать такое число без подписи нельзя: владелец примет его
 * за точное и построит на нём разговор с администратором.
 */
export const SummaryBookedComparison = z.object({
    /**
     * Сколько стоит в календаре СЕЙЧАС — по той же мерке, что и previousMinor.
     *
     * ЭТО НЕ bookedMinor. Карточка «Записано» показывает только предстоящее
     * (pending, confirmed, in_progress), а прошлая неделя вся уже отработана: там
     * те же записи лежат как completed и no_show. Сравнивать одно с другим значит
     * получать падение из ничего — к вечеру сегодняшние отработанные визиты уходят
     * из текущей суммы и остаются в прошлой, и экран показывает «минус 30 %» в день,
     * когда не отменили ни одной записи. Здесь обе стороны считаются одинаково:
     * «что стоит в календаре», то есть всё неотменённое.
     */
    currentMinor: z.number().int().nonnegative().nullable(),
    previousMinor: z.number().int().nonnegative().nullable(),
    deltaPercent: z.number().int().nullable(),
    /** Всегда true: точного ответа база дать не может, и это часть ответа. */
    approximate: z.literal(true),
});
/**
 * 5-В4 «Записано».
 *
 * ДВЕ ЦИФРЫ, КОТОРЫЕ НЕЛЬЗЯ ПУТАТЬ. «Записано» — деньги, которые ещё только предстоят:
 * отмены и неявки их уменьшат, новые записи увеличат. «Получено» — завершённые визиты,
 * число, которое меняется только назад. Слова «прогноз» на экране нет и быть не должно:
 * прогноз — это обещание, а здесь сумма уже созданных записей по их ценам.
 */
export const SummaryBooked = z.object({
    generatedAt: Instant,
    /** Глубина периода: 3, 7 или 14 дней. */
    days: z.number().int().positive(),
    /** Первый день периода — сегодня в зоне области. */
    from: LocalDate,
    /** Последний день периода включительно. */
    to: LocalDate,
    timezone: TimeZone,
    /** Сумма и число будущих записей за период (pending и confirmed). */
    bookedMinor: z.number().int().nonnegative().nullable(),
    bookedCount: z.number().int().nonnegative().nullable(),
    /** Завершённые визиты за сегодня: число меняется только назад. */
    receivedMinor: z.number().int().nonnegative().nullable(),
    receivedCount: z.number().int().nonnegative().nullable(),
    /** Заполненность смен за период. */
    occupancyPercent: z.number().int().nonnegative().nullable(),
    /**
     * Последний день, на который запись открыта хоть в одном филиале области.
     * null — горизонт не посчитался.
     */
    horizonLastDate: LocalDate.nullable(),
    /** Горизонт записи в днях: у области — наибольший из филиалов. */
    horizonDays: z.number().int().positive().nullable(),
    comparison: SummaryBookedComparison,
    rows: z.array(SummaryBookedDay),
});
export const SummaryBookedQuery = z.object({
    /** Глубина: 3, 7 или 14 дней. По умолчанию 7 (вопрос 50 владельцу). */
    days: z.union([z.literal(3), z.literal(7), z.literal(14)]).optional(),
    branchId: Uuid.optional(),
});
//# sourceMappingURL=cabinet.js.map